import axios from 'axios'

import { API_BASE_URL, API_BASE_URL_V2, AUTH_API_BASE_URL, OAUTH_API_BASE_URL } from '~/constants'
import locale from '~/locale'
import {
    ApiResponseI,
    AssetHistoryI,
    AssetsI,
    AuthResponseI,
    BatchI,
    BinI,
    BusinessAccessT,
    BusinessCustomerI,
    BusinessI,
    BusinessRequestBodyI,
    CashReportResponse,
    CatalogOrderI,
    CatalogResponseI,
    CategoryI,
    ChallanFormI,
    CollectionApiParams,
    CollectionApiResponse,
    CurrencyI,
    CustomerFilterI,
    CustomerI,
    CustomerReportFilterI,
    CustomerReportI,
    CustomLabelI,
    CustomLabelRequestBodyI,
    DeleteCustomLabelI,
    DeleteExpenseI,
    ExpensesI,
    GetBinLocationsResponseI,
    GetCustomerResponseI,
    GetRepsonseCollectionI,
    HistoryFilterI,
    HistoryI,
    HistoryInvoiceParams,
    ManufactureUpdateI,
    PaymentModeI,
    ProdcutTotalI,
    ProductFilterI,
    ProductI,
    ProductRequestBodyI,
    ProductSellingPriceRequestBodyI,
    ProfileI,
    ProfileII,
    SingleApiResponse,
    StaffFilterI,
    StaffI,
    StockActionT,
    StockReportFilterI,
    StockReportI,
    SupplierFilterI,
    SupplierI,
    TransferOrderI,
    TransferOrderIDetails,
    UpdateStockI,
    UserType,
    WarehouseI
} from '~/types'
import { localStore, StoreKey } from '~/utils/local-store'
import { $error, $log } from '~/utils/logger'

/**
 * Get auth token
 */
export const AuthToken = async (firebaseToken: string): Promise<ApiResponseI<AuthResponseI>> => {
    return axios
        .post<AuthResponseI>(`${AUTH_API_BASE_URL}/firebase`, {
            firebaseToken
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: locale.id.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            return {
                error: {
                    message: locale.id.common.request_error
                }
            }
        })
}

export const AuthTokenOtpless = async (token: string, localeText: any): Promise<ApiResponseI<AuthResponseI>> => {
    return axios
        .post<AuthResponseI>(`${AUTH_API_BASE_URL}/otpless`, {
            token
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
}

/**
 * Oauth Login
 */
export const OAuthLogin = async (
    token: string,
    user: UserType,
    localeText: any,
    setLoading?: (loading: boolean) => void
): Promise<ApiResponseI<AuthResponseI>> => {
    return axios
        .post<AuthResponseI>(`${API_BASE_URL}/api/user/login`, {
            email: user?.email,
            name: user?.displayName ?? "",
            profileImage: user?.photoURL
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            setLoading?.(false)

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
}

/**
 * Oauth Register
 */
export const OAuthRegister = async (token: string, localeText: any): Promise<ApiResponseI<AuthResponseI>> => {
    return axios
        .post<AuthResponseI>(`${OAUTH_API_BASE_URL}/firebase/register`, {
            token
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all business
 */
export const GetBusiness = async (): Promise<ApiResponseI<BusinessI[]>> => {
    return axios
        .get<BusinessI[]>(`${API_BASE_URL}/api/business`)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: locale.id.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            return {
                error: {
                    message: locale.id.common.request_error
                }
            }
        })
}

/**
 * Create business
 */
export const CreateBusiness = async (data: BusinessRequestBodyI, localeText: any): Promise<ApiResponseI<BusinessI>> => {
    return axios
        .post<{ data: any } & BusinessI>(`${API_BASE_URL}/api/business`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText.common.request_error
                }
            }
        })
}

/**
 * Update business
 */
export const UpdateBusiness = async (
    businessId: number,
    data: BusinessRequestBodyI,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios

        .patch<boolean>(`${API_BASE_URL}/api/business?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText.common.request_error
                }
            }
        })
}

/**
 * Update business language
 */
export const UpdateBusinessLanguage = async (
    businessId: number,
    language: string,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .patch<boolean>(`${API_BASE_URL}/api/business/language?businessId=${businessId}`, { language })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText.common.request_error
                }
            }
        })
}


export const BulkRemoveProduct = async (
    businessId: number,
    data: { selectedId: (string | number)[]; excludeId: (string | number)[] },
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/product/bulk-remove?businessId=${businessId}`, {
            data
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all GetProductForStockChange
 */
export const GetProductForStockChange = async (
    businessId: string,
    params: CollectionApiParams<ProductFilterI>,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'products', ProductI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'products', ProductI>>(
            `${API_BASE_URL}/api/business/product/all?businessId=${businessId}`,
            {
                params
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all Low Stock alert
 */
export const GetLowStockAlert = async (
    businessId: number,
    params: ProductFilterI,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'products', ProductI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'products', ProductI>>(
            `${API_BASE_URL}/api/business/product/all?businessId=${businessId}`,
            {
                params
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all Expenses
 */
export const GetAllExpenses = async (
    businessId: number,
    params: ProductFilterI,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'expenses', ExpensesI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'expenses', ExpensesI>>(
            `${API_BASE_URL}/api/business/product/expense?businessId=${businessId}`,
            {
                params
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all Expenses Category
 */
export const GetAllExpensesCategory = async (
    businessId: number,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', CategoryI>>> => {
    return axios
        .get<GetRepsonseCollectionI<'data', CategoryI>>(
            `${API_BASE_URL}/api/business/product/expense/category?businessId=${businessId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Add category
 */
export const CreateExpenseCategory = async (
    businessId: number,
    data: CategoryI,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .post<boolean>(`${API_BASE_URL}/api/business/product/expense/category?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Edit category
 */
export const EditExpenseCategory = async (
    businessId: number,
    data: CategoryI,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    const payload =
    {
        categoryId: data.id,
        name: data.name
    }
    return axios
        .patch<boolean>(`${API_BASE_URL}/api/business/product/expense/category?businessId=${businessId}`, payload)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}
/**
 * Add Expense
 */
export const CreateExepense = async (
    businessId: number,
    data: Partial<ExpensesI>,
    localeText: any
): Promise<ApiResponseI<ExpensesI>> => {
    return axios
        .post<{ data: any } & ExpensesI>(`${API_BASE_URL}/api/business/product/expense?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }
            return {
                error: {
                    message: localeText?.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error
                }
            }
        })
}

/**
 * Edit Expense
 */
export const EditExepense = async (
    businessId: number,
    data: Partial<ExpensesI>,
    localeText: any
): Promise<ApiResponseI<ExpensesI>> => {
    return axios
        .patch<ExpensesI>(`${API_BASE_URL}/api/business/product/expense?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error
                }
            }
        })
}

/**
 * Delete Expense
 */
export const DeleteExepense = async (
    businessId: number,
    params: DeleteExpenseI,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/product/expense?businessId=${businessId}`, {
            data: params
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Update UpdateSafetyStock
 */
export const UpdateSafetyStock = async (
    businessId: number,
    params: Partial<ProductRequestBodyI>,
    localeText: any
): Promise<ApiResponseI<ProductI>> => {
    return axios
        .patch<ProductI>(`${API_BASE_URL}/api/business/product?businessId=${businessId}`, params)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error
                }
            }
        })
}

/**
 * Get all products
 */
export const GetProduct = async (
    businessId: string,
    params: ProductFilterI,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'products', ProductI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'products', ProductI>>(
            `${API_BASE_URL}/api/business/product/all?businessId=${businessId}`,
            {
                params
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get product by id
 */
export const GetProductById = async (
    businessId: number,
    productId: number,
    localeText: any
): Promise<ApiResponseI<ProductI>> => {
    return axios
        .get<{ data: any } & ProductI>(
            `${API_BASE_URL}/api/business/product?businessId=${businessId}&productId=${productId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error
                }
            }
        })
}

/**
 * Deactivate product
 */
export const DeactivateProduct = async (
    businessId: number,
    productId: number | string,
    isActive: boolean,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .patch<boolean>(`${API_BASE_URL}/api/business/product/deactivate?businessId=${businessId}&productId=${productId}`, {
            isActive
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Create product
 */
export const CreateProduct = async (
    businessId: number,
    data: Partial<ProductRequestBodyI>,
    localeText: any
): Promise<ApiResponseI<ProductI>> => {
    const params: Omit<Partial<ProductRequestBodyI>, 'data'> = {
        buyingPrice: data.buyingPrice,
        imageUrl: data.imageUrl,
        name: data.name,
        quantity: data.quantity,
        note: data.note,
        sellingPrice: data.sellingPrice,
        sku: data.sku,
        categoryId: data.categoryId,
        categoryIds: data.categoryIds,
        unitId: data.unitId,
        expiredDate: data.expiredDate,
        description: data.description,
        bundleDetails: data.bundleDetails,
        safetyStock: data.safetyStock,
        additionalCost: data.additionalCost,
        isBundle: data.isBundle,
        customLabel: data.customLabel,
        attachment: data.attachment,
        ...(data.variants?.some((variant) => variant.warehouseDetails && variant.warehouseDetails.length > 0)
            ? {}
            : {
                warehouseDetails: data.warehouseDetails
            }),
        variants: data.variants,
        imageUrlArray: data.imageUrlArray,
        secondaryUnitId: data.secondaryUnitId,
        conversionValue: data.conversionValue,
        skuAliases: data.skuAliases,
    }

    return axios
        .post<ProductI>(`${API_BASE_URL}/api/business/product?businessId=${businessId}`, params)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error
                }
            }
        })
}

/**
 * Update product
 */
export const UpdateProduct = async (
    businessId: number,
    params: Partial<ProductRequestBodyI>,
    localeText: any
): Promise<ApiResponseI<ProductI>> => {
    return axios
        .patch<ProductI>(`${API_BASE_URL}/api/business/product?businessId=${businessId}`, params)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error
                }
            }
        })
}

// Add custoC Label
export const AddCustomLabel = async (
    businessId: number,
    params: Partial<CustomLabelRequestBodyI>,
    localeText: any
): Promise<ApiResponseI<CustomLabelI>> => {
    return axios
        .post<CustomLabelI>(`${API_BASE_URL}/api/business/product/customLabel?businessId=${businessId}`, params)

        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error
                }
            }
        })
}

export const UpdateCustomLabel = async (
    businessId: number,
    params: Partial<CustomLabelRequestBodyI>,
    localeText: any
): Promise<ApiResponseI<CustomLabelI>> => {
    return axios
        .patch<CustomLabelI>(`${API_BASE_URL}/api/business/product/customLabel?businessId=${businessId}`, params)

        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error
                }
            }
        })
}

export const DeleteCustomLabel = async (
    businessId: number,
    params: DeleteCustomLabelI,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/product/customLabel?businessId=${businessId}`, {
            data: params
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const GetCustomLabel = async (
    businessId: number,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'labels', CustomLabelI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'labels', CustomLabelI>>(
            `${API_BASE_URL}/api/business/product/customLabel?businessId=${businessId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}
/**
 * Add selling prices
 * @param businessId
 * @param productId
 * @param data
 * @returns
 */
export const AddSellingPrices = async (
    businessId: number,
    productId: string,
    data: ProductSellingPriceRequestBodyI[]
): Promise<boolean> => {
    return axios
        .post<boolean>(`${API_BASE_URL}/api/business/product/prices/bulk?businessId=${businessId}`, {
            productId,
            sellingPrices: data.map((item) => ({
                name: item.name,
                sellingPrice: item.price
            }))
        })
        .then((res) => {
            if (res.status === 200) {
                return true
            }

            return false
        })
        .catch((e) => {
            $error(e)

            return false
        })
}

/**
 * Update selling prices
 * @param businessId
 * @param data
 * @returns
 */
export const UpdateSellingPrices = async (
    businessId: number,
    data: ProductSellingPriceRequestBodyI[]
): Promise<boolean> => {
    return axios
        .patch<boolean>(`${API_BASE_URL}/api/business/product/prices/bulk?businessId=${businessId}`, {
            products: data.map((item) => ({
                productPriceID: item.id,
                productPriceName: item.name,
                sellingPrice: item.price
            }))
        })
        .then((res) => {
            if (res.status === 200) {
                return true
            }

            return false
        })
        .catch((e) => {
            $error(e)

            return false
        })
}

/**
 * Delete selling prices
 * @param businessId
 * @param data
 * @returns
 */
export const DeleteSellingPrices = async (businessId: number, data: string[]): Promise<boolean> => {
    return axios
        .delete<boolean>(`${API_BASE_URL}/api/business/product/prices/bulk?businessId=${businessId}`, {
            data: {
                productPriceIDS: data
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return true
            }

            return false
        })
        .catch((e) => {
            $error(e)

            return false
        })
}

/**
 * Delete product
 */
export const DeleteProduct = async (
    businessId: number,
    productId: string,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/product?businessId=${businessId}`, {
            data: {
                productId
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText.common.request_error
                }
            }
        })
}

/**
 * Get total product
 */
export const GetTotalProduct = async (businessId: number, localeText: any): Promise<ApiResponseI<number>> => {
    return axios
        .get<{ data: { data: []; totalCount: number } }>(
            `${API_BASE_URL}/api/business/product/total?businessId=${businessId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data.totalCount
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all units
 */
export const GetUnits = async (
    businessId: number,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', CategoryI>>> => {
    return axios
        .get<GetRepsonseCollectionI<'data', CategoryI>>(
            `${API_BASE_URL}/api/business/product/units?businessId=${businessId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all categories
 */
export const GetCategory = async (
    businessId: number,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', CategoryI>>> => {
    return axios
        .get<GetRepsonseCollectionI<'data', CategoryI>>(
            `${API_BASE_URL}/api/business/product/category?businessId=${businessId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}


/**
 * Get all categories
 */
export const GetExpenseCategory = async (
    businessId: number,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', CategoryI>>> => {
    return axios
        .get<GetRepsonseCollectionI<'data', CategoryI>>(
            `${API_BASE_URL}/api/business/product/expense/category?businessId=${businessId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const GetAssetCategory = async (
    businessId: number,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', CategoryI>>> => {
    return axios
        .get<GetRepsonseCollectionI<'data', CategoryI>>(
            `${API_BASE_URL}/api/business/v2/assets/category?businessId=${businessId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all SKU
 */
export const GetSKU = async (
    businessId: number,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', CategoryI>>> => {
    return axios
        .get<GetRepsonseCollectionI<'data', CategoryI>>(
            `${API_BASE_URL}/api/business/product/sku?businessId=${businessId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}
/**
 * Get Currency
 */
export const GetCurrency = async (
    businessId: number,
    localeText: any,
    params?: { limit?: number; offset?: number, search?: string }
): Promise<ApiResponseI<CurrencyI[]>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/currency/all`, {
            params: {
                businessId,
                limit: params?.limit,
                offset: params?.offset,
                search: params?.search
            }
        })
        .then((res) => {
            if (res.status === 200) {
                const currencies: CurrencyI[] = res.data.data.map(
                    (item: any) => ({
                        currencyId: item.id,
                        businessId: item.business_id,
                        currency_symbol: item.currency_symbol,
                        flag: item.flag,
                        currency_name: item.currency_name,
                        currency_code: item.currency_code,
                    })
                )

                return { data: currencies }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/* Get Currency
 */
export const GetCurrencySymbol = async (currencyCode: string, localeText: any): Promise<ApiResponseI<string>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/currency?code=${currencyCode}`)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const setCurrency = async (
    currencyId: number,
    currencySymbol: string,
    localeText: any,
    businessId: number
): Promise<ApiResponseI<string>> => {
    return axios
        .post(
            `${API_BASE_URL}/api/business/currency?businessId=${businessId}`,
            {
                code: currencySymbol,
                currency: {
                    id: currencyId,
                    symbol: currencySymbol
                }
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const errorMessage = e?.response?.data?.message

            return {
                error: {
                    message: errorMessage || localeText?.common?.request_error
                }
            }
        })
}
export const CreateCurrency = async (
    currencySymbol: string,
    currencyName: string,
    localeText: any,
    businessId: number
): Promise<ApiResponseI<string>> => {
    try {
        const response = await axios.post(
            `${API_BASE_URL}/api/business/currency/create?businessId=${businessId}`,
            { currency_symbol: currencySymbol.trim(), currency_name: currencyName }
        );

        if (response.status === 200) {
            return {
                data: response.data
            };
        }
        return {
            error: {
                message: localeText?.common?.request_error || 'Failed to create currency',
            },
        };
    } catch (e: any) {
        if (typeof $error === 'function') $error(e);

        const errorMessage =
            e?.response?.data?.message ||
            localeText?.common?.request_error ||
            'Failed to create currency';

        return {
            error: { message: errorMessage },
        };
    }
};
export const UpdateCurrency = async (
    businessId: number,
    localeText: any,
    payload: {
        currencyId: number
        currency_symbol: string
        currency_name: string
    }
) => {
    try {
        const response = await axios.patch(
            `${API_BASE_URL}/api/business/currency?businessId=${businessId}`,
            payload
        )

        return {
            data: response.data,
            error: null
        }
    } catch (err: any) {
        return {
            data: null,
            error: {
                message:
                    err?.response?.data?.message ||
                    localeText?.settings?.message?.currency_update_failed ||
                    'Failed to update currency'
            }
        }
    }
}
export const DeleteCurrency = async (
    businessId: number,
    localeText: any,
    currencyId: any
): Promise<ApiResponseI<string>> => {


    try {
        const response = await axios.delete(
            `${API_BASE_URL}/api/business/currency?businessId=${businessId}`, {
            data: {
                currencyId
            }
        }
        );

        if (response.status === 200) {
            return {
                data: response.data
            };
        }

        return {
            error: {
                message: localeText?.common?.request_error || 'Failed to create currency',
            },
        };
    } catch (e: any) {
        if (typeof $error === 'function') $error(e);

        const errorMessage =
            e?.response?.data?.message ||
            localeText?.common?.request_error ||
            'Failed to create currency';

        return {
            error: { message: errorMessage },
        };
    }
};
export const getTaxes = async (
    localeText: any,
    businessId: number
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/taxes?businessId=${businessId}`)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Create unit
 */
export const CreateUnit = async (
    businessId: number,
    data: CategoryI,
    localeText: any
): Promise<ApiResponseI<CategoryI>> => {
    const params: Omit<CategoryI, 'id'> = {
        name: data.name
    }

    return axios
        .post<CategoryI>(`${API_BASE_URL}/api/business/product/units?businessId=${businessId}`, params)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common.request_error
                }
            }
        })
}

// create tax rate
export const CreateTaxRate = async (
    businessId: number,
    data: any,
    localeText: any
): Promise<ApiResponseI<any>> => {
    const params: Omit<any, 'id'> = {
        name: data.label,
        rate: Number(data.tRate)
    }
    return axios

        .post<any>(`${API_BASE_URL}/api/business/taxes?businessId=${businessId}`, params)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common.request_error
                }
            }
        })
}
// create tax rate


/**
 * Create category
 */
export const CreateCategory = async (
    businessId: number,
    data: CategoryI,
    localeText: any
): Promise<ApiResponseI<CategoryI>> => {
    const params: Omit<CategoryI, 'id'> = {
        name: data.name
    }
    return axios

        .post<CategoryI>(`${API_BASE_URL}/api/business/product/category?businessId=${businessId}`, params)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common.request_error
                }
            }
        })
}

// export const CreateCategory = async (businessId: string, data: CategoryI): Promise<ApiResponseI<CategoryI>> => {
//     const params: Pick<CategoryI, 'name'> = {
//         name: data.name
//     }
//     return axios
//         .post<CategoryI>(`${API_BASE_URL}/business/${businessId}/product/category`, params)
//         .then((res) => {
//             if (res.status === 200) {
//                 return {
//                     data: res.data
//                 }
//             }

//             return {
//                 error: {
//                     message: locale.id.common.request_error
//                 }
//             }
//         })
//         .catch((e) => {
//             $error(e)

//             return {
//                 error: {
//                     message: locale.id.common.request_error
//                 }
//             }
//         })
// }


// /**
//  * Edit category
//  */
export const EditCategory = async (
    businessId: number,
    data: CategoryI,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .patch<boolean>(`${API_BASE_URL}/api/business/product/category?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Delete category
 */
export const DeleteCategory = async (
    businessId: number,
    categoryId: string,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/product/category?businessId=${businessId}`, {
            data: {
                id: categoryId
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// add prefix

export const AddPrefix = async (
    businessId: number,
    data: ChallanFormI,
    localeText: any
): Promise<ApiResponseI<string>> => {
    return axios
        .post<{ data: { prefixes: { prefixId: string } } } & ChallanFormI>(
            `${API_BASE_URL}/api/business/product/challan/prefix?businessId=${businessId}`,
            data
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data.prefixes.prefixId
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}
/**
 * Update stock
 */
// .patch<boolean>(`${API_BASE_URL}/business/${businessId}/product/stock/${type.replace('stock_', '')}`, data)

export const UpdateStock = async (
    businessId: number,
    data: UpdateStockI,
    type: StockActionT,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .patch<boolean>(
            `${API_BASE_URL}/api/business/product/stock/${type.replace('stock_', '')}?businessId=${businessId}`,
            data
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const UpdateManufacture = async (
    businessId: number,
    data: ManufactureUpdateI,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .patch<boolean>(`${API_BASE_URL}/api/business/product/stock/manufacture?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all assets
 */
export const GetAllAssets = async (
    businessId: number,
    // params: HistoryFilterI,
    localeText: any,
    params: { limit?: number; offset?: number; assetName?: string; isDeleted?: boolean } = {}
): Promise<ApiResponseI<GetRepsonseCollectionI<'assets', AssetsI>>> => {
    try {
        const response = await axios.get<{ data: any } & GetRepsonseCollectionI<'assets', AssetsI>>(
            `${API_BASE_URL}/api/business/v2/assets`,
            {
                params: {
                    businessId,
                    limit: params?.limit ?? 15,
                    offset: params?.offset ?? 0,
                    assetName: params.assetName || '',
                    isDeleted: params.isDeleted ?? false,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
            }
        );
        if (response.status === 200) {
            return {
                data: response.data.data,
            };
        }

        return {
            error: {
                message: localeText?.common?.request_error,
            },
        };
    } catch (e: any) {
        $error(e);
        const error = e?.response?.data?.message;

        return {
            error: {
                message: error || localeText?.common?.request_error,
            },
        };
    }
};

/**
 * Download Asset List as PDF
 */
export const DownloadAssetListPdf = async (
    params: {
        businessId: number
        businessName?: string
        fileName?: string
        isDeleted?: boolean
        staffId?: number
        staffName?: string
        language?: string
    }
): Promise<ApiResponseI<boolean>> => {
    return axios
        .post<Blob>(
            `/api/download/asset-list-pdf`,
            params,
            {
                responseType: 'blob',
            }
        )
        .then((res) => {
            if (res.status === 200) {
                const pdfUrl = window.URL.createObjectURL(res.data)
                const link = document.createElement('a')
                link.href = pdfUrl
                link.setAttribute('download', `${params.fileName || 'Asset-List'}.pdf`)
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(pdfUrl)
                return { data: true }
            }
            return {
                error: {
                    message: 'Failed to download PDF',
                },
            }
        })
        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: e?.response?.data?.message || 'Failed to download PDF',
                },
            }
        })
}

/**
 * Get Asset History (paginated)
 */
interface AssetHistoryResponse {
    total: number
    limit: number
    offset: number
    data: AssetHistoryI[]
}

export const GetAssetHistory = async (
    businessId: number,
    assetId: number,
    localeText: any,
    params: { limit?: number; offset?: number; search?: string } = {}
): Promise<ApiResponseI<AssetHistoryResponse>> => {
    try {
        const response = await axios.get<{ data: AssetHistoryResponse }>(
            `${API_BASE_URL}/api/business/v2/assets/history`,
            {
                params: {
                    businessId,
                    assetId,
                    limit: params?.limit ?? 15,
                    offset: params?.offset ?? 0,
                    search: params.search || '',
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
            }
        );

        if (response.status === 200) {
            return {
                data: response.data.data,
            };
        }

        return {
            error: {
                message: localeText?.common?.request_error,
            },
        };
    } catch (e: any) {
        $error(e);
        const error = e?.response?.data?.message;
        return {
            error: {
                message: error || localeText?.common?.request_error,
            },
        };
    }
};


/**
 * Get Staff Asset History (paginated) - fetches asset history for a specific staff member
 */
export const GetStaffAssetHistory = async (
    businessId: number,
    staffId: number,
    localeText: any,
    params: { limit?: number; offset?: number; search?: string } = {}
): Promise<ApiResponseI<AssetHistoryResponse>> => {
    try {
        const response = await axios.get<{ data: AssetHistoryResponse }>(
            `${API_BASE_URL}/api/business/v2/assets/history`,
            {
                params: {
                    businessId,
                    staffId,
                    limit: params?.limit ?? 15,
                    offset: params?.offset ?? 0,
                    search: params.search || '',
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
            }
        );

        if (response.status === 200) {
            return {
                data: response.data.data,
            };
        }

        return {
            error: {
                message: localeText?.common?.request_error,
            },
        };
    } catch (e: any) {
        $error(e);
        const error = e?.response?.data?.message;
        return {
            error: {
                message: error || localeText?.common?.request_error,
            },
        };
    }
};


/**
 * Get all histories
 */
export const GetHistory = async (
    businessId: number,
    params: HistoryFilterI,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'histories', HistoryI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'histories', HistoryI>>(
            `${API_BASE_URL}/api/business/product/stock/history`,
            {
                params
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const UpdateHistory = async (businessId: number, data: any, localeText?: any): Promise<ApiResponseI<boolean>> => {
    return axios
        .patch<boolean>(`${API_BASE_URL}/api/business/product/stock/history/edit?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error || 'Failed to update history'
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error || 'Failed to update history'
                }
            }
        })
}

/**
 * Get all staffs
 */
export const GetStaff = async (
    businessId: number,
    params: StaffFilterI,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'staffs', StaffI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'staffs', StaffI>>(
            `${API_BASE_URL}/api/business/staff?businessId=${businessId}`,
            {
                params
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Update stock
 */
export const UpdateStaff = async (
    businessId: number,
    payload: {
        staffId: string;
        role: string;
        access: string[];
        categoryAccess: string[];
        excludeProductCategory: string[];
        excludeWarehouseId: String[];
    },
    localeText: any
): Promise<{ data?: any; error?: { message: string } }> => {
    return axios
        .patch(`${API_BASE_URL}/api/business/staff?businessId=${businessId}`, payload)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data?.data
                };
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            };
        })
        .catch((e) => {
            $error(e);
            const error = e?.response?.data?.message;

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            };
        });
};

/**
 * Delete staff
 */
export const DeleteStaff = async (
    businessId: number,
    staffId: string,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/staff?businessId=${businessId}`, {
            data: {
                staffId
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}


/**
 * Get total staff
 */
export const GetTotalStaff = async (businessId: number, localeText: any): Promise<ApiResponseI<number>> => {
    return axios
        .get<{ data: { data: []; Total: number } }>(`${API_BASE_URL}/api/business/staff/total?businessId=${businessId}`)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data.Total
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Create join code
 */
export const CreateJoinCode = async (
    businessId: number,
    access: BusinessAccessT[],
    excludeProductCategory: any,
    excludeWarehouseId: any,
    localeText: any
): Promise<ApiResponseI<string>> => {
    return axios
        .post<{ data: { joinCode: string } }>(`${API_BASE_URL}/api/business/joincode?businessId=${businessId}`, {
            access, excludeProductCategory, excludeWarehouseId
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data.joinCode
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Check join code
 */
export const CheckJoinCode = async (joinCode: string, localeText: any): Promise<ApiResponseI<string>> => {
    return axios
        .post<{ data: { name: string } }>(`${API_BASE_URL}/api/business/checkjoin`, {
            joinCode
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data?.data.name
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Join business with code
 */

export const JoinBusinessWithCode = async (joinCode: string, localeText: any): Promise<ApiResponseI<BusinessI>> => {
    return axios
        .post<{ data: any } & BusinessI>(`${API_BASE_URL}/api/business/join`, {
            joinCode
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// Create Purchase Order
export const createPurchaseOrder = async (
    businessId: number,
    data: any,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .post(`${API_BASE_URL}/api/business/purchase-order?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// Get Purchase Order Details
export const GetPurchaseOrderDetails = async (
    businessId: number,
    purchaseOrderId: number,
    localeText: any,
    type?: any
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/${type === "sale" ? "sale" : "purchase"}-order?businessId=${businessId}&${type === "sale" ? "saleOrderId" : "purchaseOrderId"}=${purchaseOrderId}`)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// Receive Purchase Order
export const ReceivePurchaseOrder = async (
    businessId: number,
    payload: {
        purchaseOrderId: number;
        inAt: string;
        products: {
            productId: number;
            variantId: number | null;
            warehouseId: number | null;
            amount: number;
            customBuyingPrice: number;
            discounts: number;
            taxId: number | null;
            purchaseOrderItemId?: number;
            saleOrderItemId?: number;
        }[];
        note?: string;
        attachmentUrl?: string[];
        overallDiscount?: number;
        poNumber?: string;
        orderDate?: string;
        receivedDate?: string;
        saleOrderId?: any,
        soNumber?: any,
    },
    localeText: any,
    type: any
): Promise<ApiResponseI<any>> => {

    const { purchaseOrderId, poNumber, saleOrderId, soNumber, ...rest } = payload;

    const finalPayload =
        type === "sale"
            ? {
                ...rest,
                saleOrderId,
                soNumber,
            }
            : {
                ...rest,
                purchaseOrderId,
                poNumber,
            };


    return axios
        .patch(`${API_BASE_URL}/api/business/${type === "sale" ? "sale" : "purchase"}-order/receive?businessId=${businessId}`, finalPayload)
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// Delete Purchase Order
export const DeletePurchaseOrder = async (
    businessId: number,
    purchaseOrderId: number,
    localeText: any,
    type: any
): Promise<ApiResponseI<boolean>> => {

    const payload =
        type === "sale"
            ? { saleOrderId: purchaseOrderId }
            : { purchaseOrderId: purchaseOrderId };
    return axios
        .delete(`${API_BASE_URL}/api/business/${type === "sale" ? "sale" : "purchase"}-order?businessId=${businessId}`, {
            data: payload
        })
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return { data: true }
            }
            return {
                error: { message: localeText?.common?.request_error }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: { message: error || localeText?.common?.request_error }
            }
        })
}

/**
 * Get user profile
 */
export const GetProfile = async (localeText: any): Promise<ApiResponseI<ProfileII>> => {
    return axios
        .get<ProfileII>(`${API_BASE_URL}/api/user/profile`)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            console.error('Error:', e.response ? e.response.data : e.message)

            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

type UpdateProfileData = Omit<ProfileI, 'businesses'>

/**
 * Update user profile
 */
export const UpdateProfile = async (data: UpdateProfileData, localeText: any): Promise<ApiResponseI<boolean>> => {
    return axios
        .patch(`${API_BASE_URL}/api/user/profile`, { ...data, ProfileUrl: '' })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Upload Image
 */
interface UploadImageResponseI {
    data: string
}
export const UploadImage = async (formData: FormData, localeText: any): Promise<ApiResponseI<UploadImageResponseI>> => {
    return axios
        .post<{ data: any } & UploadImageResponseI>(`${API_BASE_URL}/api/upload`, formData, {
            headers: {
                'Content-Type': ' multipart/form-data'
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Delete Image
 */
export const DeleteImage = async (fileUrl: string, localeText: any): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/image`, {
            data: {
                fileUrl
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all suppliers
 */
export const GetSupplier = async (
    businessId: number,
    params: SupplierFilterI,
    localeText: any,
    isPaginated?: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', SupplierI>>> => {
    return axios
        .get<GetRepsonseCollectionI<'data', SupplierI>>(
            `${API_BASE_URL}/api/business/supplier?businessId=${businessId}`,
            {
                params: {
                    ...params,
                    isPaginated
                }
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all suppliers V2 (optimized for dropdown filters)
 */
export const GetSupplierV2 = async (
    businessId: number,
    params: SupplierFilterI,
    localeText: any,
    isPaginated?: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', SupplierI>>> => {
    return axios
        .get<GetRepsonseCollectionI<'data', SupplierI>>(
            `${API_BASE_URL}/api/business/supplier/v2?businessId=${businessId}`,
            {
                params: {
                    ...params,
                    isPaginated
                }
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Create supplier
 */
export const CreateSupplier = async (
    businessId: number,
    data: Partial<SupplierI>,
    localeText: any
): Promise<ApiResponseI<SupplierI>> => {
    return axios
        .post<SupplierI>(`${API_BASE_URL}/api/business/supplier?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Update supplier
 */
export const UpdateSupplier = async (
    businessId: number,
    data: Partial<SupplierI>,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .patch<boolean>(`${API_BASE_URL}/api/business/supplier?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Delete supplier
 */
export const DeleteSupplier = async (
    businessId: number,
    supplierId: string,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/supplier?businessId=${businessId}`, {
            data: {
                businessSupplierId: supplierId
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all customers
 */
export const GetCustomer = async (
    businessId: number,
    params: CustomerFilterI,
    localeText: any
): Promise<ApiResponseI<GetCustomerResponseI>> => {
    return axios
        .get<GetCustomerResponseI>(
            `${API_BASE_URL}/api/business/customer?businessId=${businessId}&isPaginated=true`,
            {
                params
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get unpaid customers (Receivables)
 */
export const GetUnpaidCustomers = async (
    businessId: number,
    params: any,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/customer/unpaid-customers?businessId=${businessId}`, {
            params
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Record multiple payment for a customer (Receivables)
 */
export const RecordMultiplePayment = async (
    businessId: number,
    businessCustomerId: number,
    payload: any,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .post(
            `${API_BASE_URL}/api/business/customer/customerBilling/multiple-payment?businessId=${businessId}&businessCustomerId=${businessCustomerId}`,
            payload
        )
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get unpaid suppliers (Payables/Receivables)
 */
export const GetUnpaidSuppliers = async (
    businessId: number,
    params: any,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/supplier/unpaid-suppliers?businessId=${businessId}`, {
            params
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Record multiple payment for a supplier
 */
export const RecordSupplierMultiplePayment = async (
    businessId: number,
    businessSupplierId: number,
    payload: any,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .post(
            `${API_BASE_URL}/api/business/supplier/billing/multiple-payment?businessId=${businessId}&businessSupplierId=${businessSupplierId}`,
            payload
        )
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Customer Report
 */
export const GetCustomerReport = async (
    businessId: number,
    params: CustomerReportFilterI,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', CustomerReportI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'data', CustomerReportI>>(
            `${API_BASE_URL}/api/business/reports/customer?businessId=${businessId}`,
            {
                params
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
}

/**
 * Create customer
 */
export const CreateCustomer = async (
    businessId: number,
    data: Partial<CustomerI>,
    localeText: any
): Promise<ApiResponseI<CustomerI>> => {
    return axios

        .post<CustomerI>(`${API_BASE_URL}/api/business/customer?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const errorMessage = e.response.data.message
            return {
                error: {
                    message: errorMessage
                }
            }
        })
}

/**
 * Update customer
 */

export const UpdateCustomer = async (
    businessId: number,
    data: Partial<CustomerI>,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .patch<boolean>(`${API_BASE_URL}/api/business/customer?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const errorMessage = e.response.data.message
            return {
                error: {
                    message: errorMessage
                }
            }
        })
}

/**
 * Delete customer
 */
export const DeleteCustomer = async (
    businessId: number,
    customerId: string,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/customer?businessId=${businessId}`, {
            data: {
                businessCustomerId: customerId
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// Get challan next number
export const GetChallanNextNumber = async (
    businessId: number,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', WarehouseI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'data', WarehouseI>>(
            `${API_BASE_URL}/api/business/product/challan?businessId=${businessId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// Get Warehouse
export const GetWareHouse = async (
    businessId: number,
    localeText: any,
    isActive?: boolean
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', WarehouseI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'data', WarehouseI>>(
            `${API_BASE_URL}/api/business/product/warehouse/all?businessId=${businessId}${isActive ? `&isActive=${isActive}` : ''
            }`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// Get Warehouse List
export const GetWarehouseList = async (
    businessId: number,
    localeText?: any,
    isActive?: boolean
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', WarehouseI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'data', WarehouseI>>(
            `${API_BASE_URL}/api/business/product/warehouse/list?businessId=${businessId}${isActive !== undefined ? `&isActive=${isActive}` : ''}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const GetWareHouseByProductId = async (
    businessId: number,
    productId: number,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', WarehouseI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'data', WarehouseI>>(
            `${API_BASE_URL}/api/business/product/warehouse?businessId=${businessId}&productId=${productId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

interface WarehouseAvailabilityItem {
    warehouseId: number;
    warehouseName: string;
    isAvailable: boolean;
}

interface ProductWarehouseAvailability {
    id: number;
    product_name: string;
    warehouses: WarehouseAvailabilityItem[];
}

interface WarehouseResponseData {
    totalCount: number;
    items: ProductWarehouseAvailability[];
}

interface ApiSuccessResponse<T> {
    success: true;
    message: string;
    data: T;
}
interface ApiErrorResponse {
    success?: false;
    message: string;
    data?: never;
}
type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;
type WarehouseApiAxiosResponse = {
    data: ApiResponse<WarehouseResponseData>;
    status: number;
    // ... other axios fields
};
export const GetWareHouseByProductIdAvailability = async (
    businessId: number,
    productId: number,
    localeText: Record<string, any> = {} // ← more realistic than `any`
): Promise<{
    data?: WarehouseResponseData;
    error?: { message: string };
}> => {
    try {
        const res = await axios.get<WarehouseApiAxiosResponse["data"]>(
            `${API_BASE_URL}/api/business/product/warehouseAvailability?businessId=${businessId}&productId=${productId}`
        );

        if (res.status === 200 && res.data.success) {
            return {
                data: res.data.data,
            };
        }

        return {
            error: {
                message: res.data.message || localeText.common?.request_error || "Request failed",
            },
        };
    } catch (e: any) {
        console.error(e); // ← was $error(e) — assuming it's a logger

        const message =
            e?.response?.data?.message ||
            localeText?.common?.request_error ||
            "Failed to fetch warehouse availability";

        return {
            error: { message },
        };
    }
};
export const MarkUnAvailableWarehouse = async (
    businessId: number,
    data: Partial<any>,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .post<boolean>(`${API_BASE_URL}/api/business/product/warehouse/mark-unavailable?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const errorMessage = e.response.data.message
            return {
                error: {
                    message: errorMessage
                }
            }
        })
}

export const CreateWarehouse = async (
    businessId: number,
    data: Partial<WarehouseI>,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .post<boolean>(`${API_BASE_URL}/api/business/product/warehouse?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const errorMessage = e.response.data.message
            return {
                error: {
                    message: errorMessage
                }
            }
        })
}

export const UpdateWarehouse = async (
    businessId: number,
    params: Partial<WarehouseI>,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .patch<boolean>(`${API_BASE_URL}/api/business/product/warehouse?businessId=${businessId}`, params)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const errorMessage = e.response.data.message
            return {
                error: {
                    message: errorMessage
                }
            }
        })
}

export const DeleteWarehouse = async (
    businessId: number,
    params: Partial<WarehouseI>,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete<boolean>(`${API_BASE_URL}/api/business/product/warehouse/?businessId=${businessId}`, { data: params })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const errorMessage = e.response.data.message
            return {
                error: {
                    message: errorMessage
                }
            }
        })
}

/**
 * Update Warehouse Safety Stock
 */
export const updateWarehouseSafteyStock = async (
    businessId: number,
    productId: number,
    warehouseDetails: { warehouseId: number; quantity: number; safetyStock: number | null }[],
    localeText: any,
    variantId?: number | null
): Promise<ApiResponseI<boolean>> => {
    return axios
        .patch<boolean>(
            `${API_BASE_URL}/api/business/product/warehouse/updateSafteyStock?businessId=${businessId}&productId=${productId}${variantId ? `&variantId=${variantId}` : ''}`,
            { warehouseDetails }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const errorMessage = e?.response?.data?.message
            return {
                error: {
                    message: errorMessage || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Tranfer Order
 */
export const TransferOrder = async (
    businessId: number,
    data: Partial<TransferOrderI>,
    localeText: any
): Promise<ApiResponseI<TransferOrderI>> => {
    return axios
        .post<ProductI>(`${API_BASE_URL}/api/business/product/warehouse/transferOrder/v2?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error
                }
            }
        })
}

/**
 * Tranfer Order Update
 */
export const TransferOrderUpdate = async (
    businessId: number,
    data: Partial<TransferOrderI>,
    localeText: any
): Promise<ApiResponseI<TransferOrderI>> => {
    return axios
        .patch<ProductI>(`${API_BASE_URL}/api/business/product/warehouse/transferOrder?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error
                }
            }
        })
}

/**
 * Update Transfer Order Approval Status
 */
export const UpdateTransferOrderApproval = async (
    businessId: number,
    data: {
        transferOrderId: number
        approvalStatus: 'approved' | 'rejected' | string
        reason?: string
    },
    localeText?: any
): Promise<ApiResponseI<any>> => {
    return axios
        .post(`${API_BASE_URL}/api/business/product/warehouse/transferOrder/approval?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error || 'Failed to update approval status'
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error || 'Failed to update approval status'
                }
            }
        })
}

/**
 * Get Trasfer Order
 */
export const GetTransferOrder = async (
    businessId: number,
    params: any,
    localeText: any
): Promise<ApiResponseI<{ data: any } & GetRepsonseCollectionI<'data', TransferOrderIDetails>>> => {

    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'data', TransferOrderIDetails>>(
            `${API_BASE_URL}/api/business/product/warehouse/transferOrder?businessId=${businessId}`,
            {
                params: params
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const GetTransferOrderById = async (
    businessId: number,
    orderId: number,
    localeText: any
): Promise<ApiResponseI<{ data: any } & GetRepsonseCollectionI<'data', TransferOrderIDetails>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'data', TransferOrderIDetails>>(
            `${API_BASE_URL}/api/business/product/warehouse/transferOrder?businessId=${businessId}&transferOrder=${orderId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const ClearUserState = async () => {
    localStore.remove(StoreKey.ACCESS_TOKEN)
}

export interface BusinessReportParam {
    dateType: 'day' | 'week' | 'month' | 'year'
    orderBy: 'ASC' | 'DESC'
    startDate: string
    endDate: string
    timezone: string
    warehouseId: number[]
}

export interface ReportData {
    date: string
    profit: number
    purchase: number
    sales: number
    expenses: number
    totalCogs?: number
}

/**
 * Get all business report charts
 */
export const GetBusinessReportChart = async (
    businessId: number,
    params: BusinessReportParam,
    localeText: any
): Promise<ApiResponseI<ReportData[]>> => {
    return axios
        .get<{ data: any } & ReportData[]>(`${API_BASE_URL}/api/business/reports/chart?businessId=${businessId}`, {
            params
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export interface SummaryData {
    totalCOGS: number
    currentAssetValue: number
    profitTotal: number
    purchaseTotal: number
    totalSales: number
    totalExpenses: number
    totalCurrentCOGSValue?: number
    totalSellingPrice?: number
}

/**
 * Get all business report summary
 */
export const GetBusinessReportSummary = async (
    businessId: number,
    params: { startDate: string; endDate: string; timezone: string },
    localeText: any
): Promise<ApiResponseI<SummaryData>> => {
    return axios
        .get<{ data: any } & SummaryData>(`${API_BASE_URL}/api/business/reports/summary?businessId=${businessId}`, {
            params
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all business report transaction
 */
export const GetBusinessReportTransaction = async (
    businessId: number,
    params: BusinessReportParam,
    localeText: any
): Promise<ApiResponseI<ReportData[]>> => {
    return axios
        .get<ReportData[]>(`${API_BASE_URL}/api/business/reports/transaction?businessId=${businessId}`, {
            params
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Download History Excel Report
 */
export const DownloadHistoryExcelReport = async (
    params: HistoryFilterI,
    localeText: any,
    language: string,
    myCurrency: string,
    invoiceType?: string | null | undefined
): Promise<ApiResponseI<boolean>> => {
    const searchParams = new URLSearchParams();
    // Handle params (same logic as DownloadExcelReport)
    Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
            value.forEach((v) => searchParams.append(key, String(v)));
        } else if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, String(value));
        }

    });
    return axios
        .post<Blob>(
            `/api/download/excel`,
            {
                ...params,
                localeText,
                language,
                myCurrency,
                invoiceType
            },
            {
                responseType: 'blob'
            }
        )
        .then((res) => {
            if (res.status === 200) {
                const pdfUrl = window.URL.createObjectURL(res.data)
                const link = document.createElement('a')
                link.href = pdfUrl

                link.setAttribute('download', params.fileName + '.xlsx')

                document.body.appendChild(link)

                link.click()

                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
}

export const DownloadTransferOrderExcelReport = async (
    params: any,
    localeText: any,
    language: string,
): Promise<ApiResponseI<boolean>> => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
            value.forEach((v) => searchParams.append(key, String(v)));
        } else if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, String(value));
        }

    });
    return axios
        .post<Blob>(
            `/api/download/transferorder`,
            {
                ...params,
                language,
            },
            {
                responseType: 'blob'
            }
        )
        .then((res) => {
            if (res.status === 200) {
                const pdfUrl = window.URL.createObjectURL(res.data)
                const link = document.createElement('a')
                link.href = pdfUrl

                link.setAttribute('download', params.fileName + '.xlsx')

                document.body.appendChild(link)

                link.click()

                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
}

export const DownloadTransferOrderPdfReport = async (
    params: any,
    localeText: any,
    language: string,
): Promise<ApiResponseI<boolean>> => {
    return axios
        .post<Blob>(
            `/api/download/transferorder-pdf`,
            {
                ...params,
                language,
            },
            {
                responseType: 'blob'
            }
        )
        .then((res) => {
            if (res.status === 200) {
                const pdfUrl = window.URL.createObjectURL(res.data)
                const link = document.createElement('a')
                link.href = pdfUrl

                link.setAttribute('download', params.fileName + '.pdf')

                document.body.appendChild(link)

                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(pdfUrl)

                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
}

export const DownloadReceivablesPdfReport = async (
    params: any,
    localeText: any,
    language: string
): Promise<ApiResponseI<boolean>> => {
    return axios
        .post<Blob>(
            `/api/download/receivables-pdf`,
            {
                ...params,
                language
            },
            {
                responseType: 'blob'
            }
        )
        .then((res) => {
            if (res.status === 200) {
                const pdfUrl = window.URL.createObjectURL(res.data)
                const link = document.createElement('a')
                link.href = pdfUrl
                link.setAttribute('download', (params.fileName || 'Receivables') + '.pdf')
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(pdfUrl)

                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error || 'Failed to download PDF'
                }
            }
        })
        .catch((e) => {
            $error(e)

            return {
                error: {
                    message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to download PDF'
                }
            }
        })
}

export const DownloadProductPdfReport = async (
    params: ProductFilterI,
    filename: string,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .post<Blob>(
            `/api/download/pdf`,
            {
                params
            },
            {
                responseType: 'blob'
            }
        )
        .then((res) => {
            if (res.status === 200) {
                const pdfUrl = window.URL.createObjectURL(res.data)
                const link = document.createElement('a')
                link.href = pdfUrl
                link.setAttribute('download', `${filename}.pdf`)
                document.body.appendChild(link)
                link.click()
                return { data: true }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
}

export const DownloadAccountingPdfReport = async (
    params: any,
    filename: string,
    localeText: any,
    myCurrency?: string,
    currencyCode?: string
): Promise<ApiResponseI<boolean>> => {
    return axios
        .post<Blob>(
            `/api/download/accounting-pdf`,
            {
                params,
                myCurrency,
                currencyCode,
                localeText
            },
            {
                responseType: 'blob'
            }
        )
        .then((res) => {
            if (res.status === 200) {
                const pdfUrl = window.URL.createObjectURL(res.data)
                const link = document.createElement('a')
                link.href = pdfUrl
                link.setAttribute('download', `${filename}.pdf`)
                document.body.appendChild(link)
                link.click()
                return { data: true }
            }
            return { error: { message: localeText?.common?.request_error } }
        })
        .catch((e) => {
            $error(e)
            return { error: { message: localeText?.common?.request_error } }
        })
}


export const DownloadInvoicePdf = async (
    params: HistoryInvoiceParams,
    filename: string,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .post(`/api/download/invoice`, { ...params }, { responseType: 'blob' })
        .then((res) => {
            if (res.status === 200) {
                const blob = new Blob([res.data], { type: 'application/pdf' }) // Explicitly set type
                const pdfUrl = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = pdfUrl
                link.setAttribute('download', `${filename}.pdf`)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link) // Cleanup
                URL.revokeObjectURL(pdfUrl) // Free up memory
                return { data: true }
            }
            return { error: { message: localeText?.common?.request_error } }
        })

        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
}

export interface DownloadManufactureParams {
    businessId: number
    mfgId: number
    myCurrency: string
    currencyCode: string
    lang: string
    showAdditionalCost?: boolean
    showBuyingPrice?: boolean
}

export const DownloadManufacturePdf = async (
    params: DownloadManufactureParams,
    filename: string,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .post(`/api/download/manufacture`, { ...params }, { responseType: 'blob' })
        .then((res) => {
            if (res.status === 200) {
                const blob = new Blob([res.data], { type: 'application/pdf' })
                const pdfUrl = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = pdfUrl
                link.setAttribute('download', `${filename}.pdf`)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(pdfUrl)
                return { data: true }
            }
            return { error: { message: localeText?.common?.request_error } }
        })
        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
}


/**
 * Get stock report
 */
export const GetStockReport = async (
    businessId: number,
    params: StockReportFilterI,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', StockReportI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'data', StockReportI>>(
            `${API_BASE_URL}/api/business/product/stock/report?businessId=${businessId}`,
            {
                params
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get stock report
 */
export const GetProductTotal = async (
    businessId: number,
    localeText: any
): Promise<ApiResponseI<{ total: ProdcutTotalI }>> => {
    return axios
        .get<{ data: any } & { total: ProdcutTotalI }>(
            `${API_BASE_URL}/api/business/product/v2/total?businessId=${businessId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data.total
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get stock report
 */
export const GetLedger = async (
    businessId: number,
    params: StockReportFilterI,
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', StockReportI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'data', StockReportI>>(
            `${API_BASE_URL}/api/business/product/stock/report?businessId=${businessId}`,
            {
                params
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Download Excel Report
 */
export const DownloadExcelReport: <T extends Record<string, any>>(
    params: T,
    fileName: string,
    template: string,
    endpoint: string,
    mapToData?: string | string[],
    localeText?: any,
    language?: string,
    myCurrency?: string,
    extraData?: Record<string, any>
) => Promise<ApiResponseI<boolean>> = async (
    params,
    fileName,
    template,
    endpoint,
    mapToData,
    localeText,
    language,
    myCurrency,
    extraData
): Promise<ApiResponseI<boolean>> => {
        const searchParams = new URLSearchParams()
        Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
                // ✅ For any key with array values, append each item (like staffId, id, etc.)
                value.forEach((v) => searchParams.append(key, String(v)));
            } else if (value !== undefined && value !== null && value !== '') {
                searchParams.append(key, String(value));
            }
        });
        return axios
            .post<Blob>(
                `/api/download/report`,
                {
                    fileName: fileName,
                    template,
                    endpoint: `${endpoint}${searchParams.toString() ? '&' + searchParams.toString() : ''}`,
                    mapToData,
                    localeText,
                    language,
                    myCurrency,
                    extraData
                },
                {
                    responseType: 'blob'
                }
            )
            .then(async (res) => {
                if (res.status === 200) {
                    try {
                        const buffer = Array.from(new Uint8Array(await res.data.arrayBuffer()))
                        AppChannel.postMessage('download:' + buffer)
                        $log('Download', 'Post Message', buffer)
                    } catch (e) {
                        const fileUrl = window.URL.createObjectURL(res.data)
                        const link = document.createElement('a')
                        link.href = fileUrl

                        link.setAttribute('download', fileName + '.xlsx')

                        document.body.appendChild(link)

                        link.click()
                        $log('Download', 'Default')
                    }

                    return {
                        data: true
                    }
                }

                return {
                    error: {
                        message: localeText?.common?.request_error
                    }
                }
            })
            .catch((e) => {
                $error(e)

                return {
                    error: {
                        message: localeText?.common?.request_error
                    }
                }
            })
    }

/**
 * Download Daily Cash Report as PDF (client-side, no backend route needed)
 */
export const DownloadDailyCashPdfReport = async (
    prefetchedData: any,           // fullDataResponse?.data  (same shape as Excel)
    fileName: string,
    localeText?: any,
    myCurrency?: string,
    startDate?: string,
    endDate?: string,
    language?: string              // e.g. 'en', 'hi', 'ar', 'fr' — same as Excel
): Promise<ApiResponseI<boolean>> => {
    try {
        const rows: any[] = Array.isArray(prefetchedData?.data) ? prefetchedData.data : []
        const meta: any = prefetchedData?.meta || {}
        const grandTotals = meta?.grandTotal || {}
        const dateWiseTotalMap: Record<string, any> = meta?.dateWiseTotals || {}

        const reportTitle = localeText?.dailyCashReport?.title || 'Daily Cash Report'
        const lang = language || 'en'
        const dayjs = (await import('dayjs')).default

        // Load language-specific dayjs locale (same approach as the rest of the app)
        try {
            if (lang !== 'en') {
                // dayjs locale codes match the app's language codes directly
                // e.g. 'hi', 'ar', 'fr', 'de', 'es', 'zh', 'ja', 'ko', etc.
                await import(`dayjs/locale/${lang}.js`)
                dayjs.locale(lang)
            } else {
                dayjs.locale('en')
            }
        } catch {
            // If locale not found (e.g. 'gu', 'tl', 'km'), fall back to 'en'
            dayjs.locale('en')
        }

        let dateRangeStr = ''
        if (startDate && endDate) {
            const startLabel = dayjs(startDate).format('DD MMM YYYY')
            const endLabel = dayjs(endDate).format('DD MMM YYYY')
            dateRangeStr = `${startLabel} \u2013 ${endLabel}`
        }

        // ── Grand-total summary line ──
        const sym = myCurrency || ''
        const fmt = (val: number) =>
            `${sym}${Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

        const totalSalesLabel = `${localeText?.dailyCashReport?.totalSales || 'Total Sales'}: ${fmt(grandTotals.saleAmount || 0)}`
        const cashReceivedLabel = `${localeText?.dailyCashReport?.cashReceived || 'Cash/Bank Received'}: ${fmt(grandTotals.receiveAmount || 0)}`
        const outstandingLabel = `${localeText?.dailyCashReport?.outstanding || 'Outstanding'}: ${fmt(grandTotals.debitedAmount || 0)}`

        // ── Build date list (DESC) — mirrors UI/Excel filler logic ──
        const parseInTz = (d: string) => {
            if (!d) return dayjs()
            return dayjs(d)
        }

        const normalizedDwt: Record<string, any> = {}
        Object.keys(dateWiseTotalMap).forEach(k => {
            normalizedDwt[parseInTz(k).format('YYYY-MM-DD')] = dateWiseTotalMap[k]
        })

        const allDates: string[] = []
        if (startDate && endDate) {
            const s = parseInTz(startDate).startOf('day')
            const e = parseInTz(endDate).startOf('day')
            const diff = e.diff(s, 'day')
            for (let i = 0; i <= diff; i++) {
                allDates.push(s.add(i, 'day').format('YYYY-MM-DD'))
            }
            allDates.reverse()
        }

        // ── Build table rows ──
        const tableRows: string[][] = []

        const buildProductStr = (item: any): string => {
            if (!Array.isArray(item.productsSold) || item.productsSold.length === 0) return '-'
            return item.productsSold
                .map((p: any) => {
                    const name = p.variantName ? `${p.productName} (${p.variantName})` : p.productName
                    return `${name} x${p.quantity}`
                })
                .join(', ')
        }

        // Track which row indices are "total" rows and which is the grand-total row
        const totalRowIndices = new Set<number>()
        let grandTotalRowIndex = -1

        if (allDates.length > 0) {
            allDates.forEach(dateKey => {
                const itemsForDate = rows.filter(
                    (item: any) => parseInTz(item.date).format('YYYY-MM-DD') === dateKey
                )

                if (itemsForDate.length > 0) {
                    itemsForDate.forEach((item: any) => {
                        const isDummy = !item.customerName || item.customerName === '-'
                        tableRows.push([
                            dayjs(item.date).format('DD MMM YYYY'),
                            item.customerName || '-',
                            buildProductStr(item),
                            isDummy ? '-' : fmt(item.saleAmount),
                            isDummy || item.receiveAmount === 0 ? '-' : fmt(item.receiveAmount),
                            isDummy || item.debitedAmount === 0 ? '-' : fmt(item.debitedAmount),
                            Array.isArray(item.paymentModes) && item.paymentModes.length > 0 ? item.paymentModes.join(', ') : '-',
                        ])
                    })
                } else if (!normalizedDwt[dateKey]) {
                    tableRows.push([dayjs(dateKey).format('DD MMM YYYY'), '-', '-', '-', '-', '-', '-'])
                }

                if (normalizedDwt[dateKey]) {
                    const dt = normalizedDwt[dateKey]
                    totalRowIndices.add(tableRows.length)
                    tableRows.push([
                        '',
                        `${localeText?.dailyCashReport?.totalLabel || 'Total'} \u2014 ${dayjs(dateKey).format('DD MMM YYYY')}`,
                        '',
                        fmt(dt.saleAmount),
                        fmt(dt.receiveAmount),
                        fmt(dt.debitedAmount),
                        '',
                    ])
                }
            })
        } else {
            rows.forEach((item: any) => {
                const isDummy = !item.customerName || item.customerName === '-'
                tableRows.push([
                    dayjs(item.date).format('DD MMM YYYY'),
                    item.customerName || '-',
                    buildProductStr(item),
                    isDummy ? '-' : fmt(item.saleAmount),
                    isDummy || item.receiveAmount === 0 ? '-' : fmt(item.receiveAmount),
                    isDummy || item.debitedAmount === 0 ? '-' : fmt(item.debitedAmount),
                    Array.isArray(item.paymentModes) && item.paymentModes.length > 0 ? item.paymentModes.join(', ') : '-',
                ])
            })
        }

        // ── Grand-total footer row ──
        grandTotalRowIndex = tableRows.length
        tableRows.push([
            '',
            localeText?.dailyCashReport?.grandTotalAllDates || 'GRAND TOTAL (all dates)',
            '',
            fmt(grandTotals.saleAmount || 0),
            fmt(grandTotals.receiveAmount || 0),
            fmt(grandTotals.debitedAmount || 0),
            '',
        ])

        const colHeaders = [
            localeText?.dailyCashReportColumns?.date || 'Date',
            localeText?.dailyCashReportColumns?.customer || 'Customer',
            localeText?.dailyCashReportColumns?.products || 'Products',
            localeText?.dailyCashReportColumns?.sales || 'Sales',
            localeText?.dailyCashReportColumns?.receivedShort || 'Received',
            localeText?.dailyCashReportColumns?.due || 'Due',
            localeText?.dailyCashReportColumns?.mode || 'Mode',
        ]

        const payload = {
            title: reportTitle,
            dateRange: dateRangeStr,
            totalSalesLabel,
            cashReceivedLabel,
            outstandingLabel,
            colHeaders,
            tableRows,
            totalRowIndices: Array.from(totalRowIndices),
            grandTotalRowIndex,
            language: lang
        }

        const res = await axios.post('/api/download/daily-cash-pdf', payload, {
            responseType: 'blob'
        })

        if (res.status === 200) {
            try {
                // If it's a mobile app webview (AppChannel exists)
                const buffer = Array.from(new Uint8Array(await res.data.arrayBuffer()))
                if (typeof AppChannel !== 'undefined') {
                    AppChannel.postMessage('download:' + buffer)
                } else {
                    throw new Error("No AppChannel")
                }
            } catch (e) {
                // Browser download
                const fileUrl = window.URL.createObjectURL(res.data)
                const link = document.createElement('a')
                link.href = fileUrl
                link.setAttribute('download', fileName + '.pdf')
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            }
        }

        return { data: true }
    } catch (e: any) {
        $error(e)
        return { error: { message: localeText?.common?.request_error || 'Failed to generate PDF' } }
    }
}

/**
 * Download Purchase Order Excel Report
 */
export const DownloadPurchaseOrderExcelReport = async (
    params: any,
    businessId: string,
    type: any,
    adminS: boolean,
    fileName: string,
    language?: string
): Promise<ApiResponseI<boolean>> => {
    console.log("called");
    const currentLocale = (locale[(language || 'en') as keyof typeof locale] || locale.en) as any

    return axios
        .post<Blob>(
            `/api/download/purchaseorder`,
            {
                ...params,
                businessId,
                type,
                adminS,
                fileName,
                language
            },
            {
                responseType: 'blob'
            }
        )
        .then((res) => {
            if (res.status === 200) {
                const pdfUrl = window.URL.createObjectURL(res.data)
                const link = document.createElement('a')
                link.href = pdfUrl

                link.setAttribute('download', fileName + '.xlsx')

                document.body.appendChild(link)

                link.click()

                return {
                    data: true
                }
            }

            return {
                error: {
                    message: currentLocale?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            return {
                error: {
                    message: currentLocale?.common?.request_error
                }
            }
        })
}
/**
 * Download Product Excel Report
 */
export const DownloadProductExcelReport = async (
    params: ProductFilterI,
    businessId: string,
    fileName: string,
    localeText: any,
    language: string,
    myCurrency: string,
    invoiceType?: string | null | undefined,
    selectedFields?: string[]
): Promise<ApiResponseI<boolean>> => {
    return axios
        .post<Blob>(
            `/api/download/product`,
            {
                params,
                businessId: businessId,
                fileName: fileName,
                localeText,
                language,
                myCurrency,
                invoiceType,
                selectedFields
            },
            {
                responseType: 'blob'
            }
        )
        .then((res) => {
            if (res.status === 200) {
                const pdfUrl = window.URL.createObjectURL(res.data)
                const link = document.createElement('a')
                link.href = pdfUrl

                link.setAttribute('download', fileName + '.xlsx')

                document.body.appendChild(link)

                link.click()

                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
}

/**
 * Download Product List PDF Report
 */
export const DownloadProductListPdfReport = async (
    params: ProductFilterI,
    businessId: string,
    businessName: string,
    fileName: string,
    localeText: any,
    language: string,
    myCurrency: string,
    invoiceType?: string | null | undefined,
    selectedFields?: string[]
): Promise<ApiResponseI<boolean>> => {
    return axios
        .post<Blob>(
            `/api/download/product-list-pdf`,
            {
                params,
                businessId: businessId,
                businessName,
                fileName: fileName,
                localeText,
                language,
                myCurrency,
                invoiceType,
                selectedFields
            },
            {
                responseType: 'blob'
            }
        )
        .then((res) => {
            if (res.status === 200) {
                const pdfUrl = window.URL.createObjectURL(res.data)
                const link = document.createElement('a')
                link.href = pdfUrl

                link.setAttribute('download', fileName + '.pdf')

                document.body.appendChild(link)

                link.click()

                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get collection data
 * @param url
 * @param params
 * @returns
 */
export const getCollection = async <T, P, F extends string = 'data', W extends boolean = false>(
    url: string,
    params?: CollectionApiParams<P>,
    baseUrl: keyof typeof baseUrlMap = 'v1'
) => {
    const response = await axios.get<
        W extends false
        ? CollectionApiResponse<T, F> & { data: any }
        : { data?: CollectionApiResponse<T, F> & { data: any } }
    >(`${baseUrlMap[baseUrl]}${url}`, {
        params
    })

    return response?.data?.data
}
/**
 * Get single data
 * @param url
 * @returns
 */
export const getSingle = async <T>(url: string, baseUrl: keyof typeof baseUrlMap = 'v1') => {
    const response = await axios.get<SingleApiResponse<T>>(`${baseUrlMap[baseUrl]}${url}`)
    return response.data
}

export const GetProductQuantity = async (businessId: number, productId: number, localeText: any, variantId?: number | string | null): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/product/quantity`, {
            params: {
                businessId,
                productId,
                ...(variantId && { variantId })
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
}
const baseUrlMap = {
    v1: API_BASE_URL,
    v2: API_BASE_URL_V2,
    auth: AUTH_API_BASE_URL,
    oauth: OAUTH_API_BASE_URL
}

export const getPaymentByHistory = async ({
    businessId,
    historyId,
    type
}: {
    businessId: number
    historyId: number
    type: 'stock_in' | 'stock_out'
}) => {
    const url =
        type === 'stock_in'
            ? `${API_BASE_URL}/api/business/supplier/Billing/history`
            : `${API_BASE_URL}/api/business/customer/customerBilling/history`

    const { data } = await axios.get(url, {
        params: {
            businessId,
            historyId
        }
    })

    return data
}

export const getPaymentModes = async (params: {
    businessId: number
}) => {
    const res = await axios.get<{
        success: boolean
        data: PaymentModeI[]
    }>(
        `${API_BASE_URL}/api/business/paymentList`,
        { params }
    )

    return res.data
}

export const DeleteExpenseCategory = async (
    businessId: number,
    categoryId: string,
    localeText: any
): Promise<ApiResponseI<boolean>> => {

    return axios
        .delete(`${API_BASE_URL}/api/business/product/expense/category?businessId=${businessId}&categoryId=${categoryId}`)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const CreateAssetCategory = async (
    businessId: number,
    data: CategoryI,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .post<boolean>(`${API_BASE_URL}/api/business/v2/assets/category?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const EditAssetCategory = async (
    businessId: number,
    data: CategoryI,
    localeText: any,
): Promise<ApiResponseI<boolean>> => {
    const payload =
    {
        categoryId: data.id,
        name: data.name
    }
    return axios
        .patch<boolean>(`${API_BASE_URL}/api/business/v2/assets/category?businessId=${businessId}`, payload)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const DeleteAssetCategory = async (
    businessId: number,
    categoryId: string,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/v2/assets/category`, {
            params: {
                businessId
            },
            data: {
                categoryId
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}
export const GetWarehouseReport = async (
    businessId: number,
    params: {
        startDate?: string;
        endDate?: string;
        sortBy?: string;
        orderBy?: string;
        timezone?: string;
        limit?: number;
        offset?: number;
        productName?: string;
        warehouseId?: number | number[];
    },
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', StockReportI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'data', StockReportI>>(
            `${API_BASE_URL}/api/business/reports/warehouse`,
            {
                params: {
                    businessId,
                    ...params
                }
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}


export const GetDailyReport = async (
    businessId: number,
    params: {
        startDate?: string;
        endDate?: string;
        sortBy?: string;
        orderBy?: string;
        timezone?: string;
        limit?: number;
        offset?: number;
        search?: string;
        warehouseId?: number | number[];
        turnover?: string;
        productId?: number;
    },
    localeText: any
): Promise<ApiResponseI<GetRepsonseCollectionI<'data', StockReportI>>> => {
    return axios
        .get<{ data: any } & GetRepsonseCollectionI<'data', StockReportI>>(
            `${API_BASE_URL}/api/business/reports/daily-stock`,
            {
                params: {
                    businessId,
                    ...params
                }
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Download Daily Stock Excel Report
 */
export const DownloadDailyStockReport = async (
    params: any,
    businessId: number,
    fileName: string,
    localeText: any,
    language: string,
    myCurrency: string
): Promise<ApiResponseI<boolean>> => {
    return axios
        .post<Blob>(
            `/api/download/daily-stock-report`,
            {
                params,
                businessId,
                fileName,
                localeText,
                language,
                myCurrency
            },
            {
                responseType: 'blob'
            }
        )
        .then((res) => {
            if (res.status === 200) {
                const pdfUrl = window.URL.createObjectURL(res.data)
                const link = document.createElement('a')
                link.href = pdfUrl

                link.setAttribute('download', fileName + '.xlsx')

                document.body.appendChild(link)

                link.click()

                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
}

/**
 * Update Purchase Order
 */
export const UpdatePurchaseOrderApi = async (
    businessId: number,
    data: any,
    localeText: any,
    type: any
): Promise<ApiResponseI<any>> => {
    const endpoint =
        type === "sale"
            ? "/api/business/sale-order"
            : "/api/business/purchase-order";

    return axios
        .patch(`${API_BASE_URL}${endpoint}?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Cancel or Reopen Purchase / Sale Order
 */
export const CancelOrReopenPurchaseOrderApi = async (
    businessId: number,
    data: { saleOrderId?: number; purchaseOrderId?: number; status: 'cancelled' | 'draft' },
    localeText: any,
    type?: any
): Promise<ApiResponseI<any>> => {
    return axios
        .patch(`${API_BASE_URL}/api/business/sale-order/cancel?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data?.data || res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Create Purchase Order
 */
export const CreatePurchaseOrderApi = async (
    businessId: number,
    data: any,
    localeText: any,
    type: any
): Promise<ApiResponseI<any>> => {
    const endpoint =
        type === "sale"
            ? "/api/business/sale-order"
            : "/api/business/purchase-order";

    return axios
        .post(`${API_BASE_URL}${endpoint}?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all Purchase Orders
 */
export const GetPurchaseOrders = async (
    businessId: number,
    type: any,
    params: any,
    localeText: any
): Promise<ApiResponseI<any>> => {

    const endpoint =
        type === "sale"
            ? "/api/business/sale-order"
            : "/api/business/purchase-order";

    return axios
        .get(`${API_BASE_URL}${endpoint}?businessId=${businessId}`, {
            params
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all Purchase Orders for Admin Approval
 */
export const GetPurchaseOrdersApproval = async (
    businessId: number,
    type: any,
    params: any,
    localeText: any,
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/${type === "sale" ? "sale" : "purchase"}-order/approval?businessId=${businessId}`, {
            params
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Perform action (Confirm/Reject) on Purchase Order Approval
 */
export const ActionPurchaseOrderApproval = async (
    businessId: number,
    data: { purchaseOrderId?: number; saleOrderId?: number; action: string; reason?: string },
    localeText: any,
    type?: string
): Promise<ApiResponseI<any>> => {
    const orderType = type === 'sale' ? 'sale' : 'purchase';
    const payload = { ...data };

    // If it's a sale order and purchaseOrderId was provided, rename it to saleOrderId if it helps, 
    // but the component should ideally handle this. Let's make it robust.
    if (type === 'sale' && payload.purchaseOrderId) {
        payload.saleOrderId = payload.purchaseOrderId;
        delete payload.purchaseOrderId;
    }

    return axios
        .patch(`${API_BASE_URL}/api/business/${orderType}-order/approval?businessId=${businessId}`, payload)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}
export const GetCatalogDetails = async (
    slug: string,
    localeText: any
): Promise<ApiResponseI<{ data: CatalogResponseI }>> => {

    return axios
        .get<{ data: CatalogResponseI }>(
            `${API_BASE_URL}/api/business/catalog/catalog-details?slug=${slug}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const GetCatalogProducts = async (
    slug: string,
    name: string,
    localeText: any,
    categoryId?: string | number | (string | number)[],
    limit?: number | { limit?: number; offset?: number },
    offset?: number
): Promise<ApiResponseI<{ data: CatalogResponseI; meta?: { total?: number; pagination?: { limit?: number; offset?: number; total?: number; totalPages?: number; count?: number } } }>> => {
    let finalLimit: number | undefined;
    let finalOffset: number | undefined;

    if (typeof limit === 'object' && limit !== null) {
        finalLimit = limit.limit;
        finalOffset = limit.offset;
    } else {
        finalLimit = limit;
        finalOffset = offset;
    }

    let url = `${API_BASE_URL}/api/business/catalog/catalog-products?slug=${slug}&name=${name || ''}`;
    if (categoryId !== undefined && categoryId !== null && categoryId !== "") {
        const catParam = Array.isArray(categoryId) ? categoryId.join(",") : String(categoryId);
        if (catParam) {
            url += `&categoryId=${encodeURIComponent(catParam)}`;
        }
    }
    if (finalLimit !== undefined && finalLimit !== null) {
        url += `&limit=${finalLimit}`;
    }
    if (finalOffset !== undefined && finalOffset !== null) {
        url += `&offset=${finalOffset}`;
    }
    return axios
        .get<{ data: CatalogResponseI; meta?: { total?: number; pagination?: { limit?: number; offset?: number; total?: number; totalPages?: number; count?: number } } }>(url)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const CreateCustomerWithoutAuth = async (
    businessId: number,
    data: Partial<CustomerI>,
    localeText: any
): Promise<ApiResponseI<{ data: BusinessCustomerI }>> => {
    return axios

        .post<{ data: BusinessCustomerI }>(`${API_BASE_URL}/api/business/customer/createBusinessCustomer?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText.common.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const errorMessage = e.response.data.message
            return {
                error: {
                    message: errorMessage
                }
            }
        })
}

export const CreateCatalogOrder = async (
    businessId: number,
    data: any,
    localeText: any
): Promise<ApiResponseI<{ data: any }>> => {
    return axios
        .post<{ data: any }>(
            `${API_BASE_URL}/api/business/catalog/order?businessId=${businessId}`,
            data
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const GetCatalogOrders = async (
    businessId: number,
    params?: { customerId?: number; catalogId?: number; catalogOrderId?: number; limit?: number; offset?: number; search?: string; status?: string } | number,
    catalogId?: number,
    localeText?: any
): Promise<ApiResponseI<{ data: any }>> => {
    let url = `${API_BASE_URL}/api/business/catalog/order?businessId=${businessId}`
    if (typeof params === 'object' && params !== null) {
        if (params.customerId) url += `&customerId=${params.customerId}`
        if (params.catalogId) url += `&catalogId=${params.catalogId}`
        if (params.catalogOrderId) url += `&catalogOrderId=${params.catalogOrderId}`
        if (params.limit) url += `&limit=${params.limit}`
        if (params.offset) url += `&offset=${params.offset}`
        if (params.search) url += `&search=${encodeURIComponent(params.search)}`
        if (params.status) url += `&status=${encodeURIComponent(params.status)}`
    } else if (typeof params === 'number') {
        url += `&customerId=${params}`
        if (catalogId) url += `&catalogId=${catalogId}`
    }
    return axios
        .get<{ data: any }>(url)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// update catalog order status
export const UpdateCatalogOrderStatus = async (
    businessId: number,
    catalogOrderId: number,
    status: string,
    localeText?: any
): Promise<ApiResponseI<{ data: any }>> => {
    return axios
        .patch<{ data: any }>(
            `${API_BASE_URL}/api/business/catalog/order?businessId=${businessId}&catalogOrderId=${catalogOrderId}`,
            { status }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// get customer transaction
export const GetCustomerTransaction = async (
    businessId: number,
    customerId: number,
    localeText: any
): Promise<ApiResponseI<{ data: CatalogOrderI[] }>> => {
    return axios
        .get<{ data: CatalogOrderI[] }>(
            `${API_BASE_URL}/api/business/customer/customerBilling?businessId=${businessId}&businessCustomerId=${customerId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// get supplier transaction
export const GetSupplierTransaction = async (
    businessId: number,
    customerId: number,
    localeText: any
): Promise<ApiResponseI<{ data: CatalogOrderI[] }>> => {
    return axios
        .get<{ data: CatalogOrderI[] }>(
            `${API_BASE_URL}/api/business/supplier/billing?businessId=${businessId}&businessSupplierId=${customerId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// get customer opening balance bill
export const GetCustomerOpeningBalanceBill = async (
    businessId: number,
    customerId: number,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .get<any>(
            `${API_BASE_URL}/api/business/customer/customerBilling/opening-balance`,
            {
                params: {
                    businessId,
                    businessCustomerId: customerId
                }
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// get supplier opening balance bill
export const GetSupplierOpeningBalanceBill = async (
    businessId: number,
    customerId: number,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .get<any>(
            `${API_BASE_URL}/api/business/supplier/billing/opening-balance`,
            {
                params: {
                    businessId,
                    businessSupplierId: customerId
                }
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}


export const GetHistoryById = async (
    businessId: number,
    customerId: number,
    localeText: any,
    params?: any
): Promise<ApiResponseI<{ data: any }>> => {
    return axios
        .get<{ data: any }>(
            `${API_BASE_URL}/api/business/customer/history`,
            {
                params: {
                    businessId,
                    businessCustomerId: customerId,
                    ...params
                }
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// get supplier history
export const GetSuuplierHistoryById = async (
    businessId: number,
    supplierId: number,
    localeText: any,
    params?: any
): Promise<ApiResponseI<{ data: any }>> => {
    return axios
        .get<{ data: any }>(
            `${API_BASE_URL}/api/business/supplier/history`,
            {
                params: {
                    businessId,
                    businessSupplierId: supplierId,
                    ...params
                }
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// get stock history
export const GetStockHistoryById = async (
    businessId: number,
    historyId: number,
    localeText: any,
    inAt?: string,
    timezone?: string
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/product/stock/history`, {
            params: {
                businessId,
                historyId,
                inAt,
                timezone,
                offset: 0
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

// customer attributes
export const GetCustomerAttributes = async (
    businessId: number,
    localeText: any,
    params?: { offset?: number; limit?: number },
    type: 'customer' | 'supplier' = 'customer'
): Promise<ApiResponseI<any>> => {
    return axios
        .get(
            `${API_BASE_URL}/api/business/${type}/${type}Attribute`,
            {
                params: { ...params, businessId }
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const CreateCustomerAttributes = async (
    businessId: number,
    payload: any,
    localeText: any,
    type: 'customer' | 'supplier' = 'customer'
): Promise<ApiResponseI<any>> => {
    return axios
        .post(
            `${API_BASE_URL}/api/business/${type}/${type}Attribute?businessId=${businessId}`,
            payload
        )
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const UpdateCustomerAttribute = async (
    businessId: number,
    payload: any,
    localeText: any,
    type: 'customer' | 'supplier' = 'customer'
): Promise<ApiResponseI<any>> => {
    return axios
        .patch(
            `${API_BASE_URL}/api/business/${type}/${type}Attribute?businessId=${businessId}`,
            payload
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const DeleteCustomerAttributes = async (
    businessId: number,
    labelIds: number[],
    localeText: any,
    type: 'customer' | 'supplier' = 'customer'
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/${type}/${type}Attribute?businessId=${businessId}`, {
            data: { labelIds }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all batches
 */
export const GetBatches = async (
    businessId: number,
    localeText: any,
    params?: { offset?: number; limit?: number; search?: string; approvalStatus?: string }
): Promise<ApiResponseI<any>> => {
    return axios
        .get<{ data: BatchI[] }>(`${API_BASE_URL}/api/business/batch`, {
            params: { ...params, businessId }
        })
        .then((res) => {
            if (res.status === 200) {
                // handle both {data:[], batch:[]} and [] formats
                const responseData = res.data as any;
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get batch by id
 */
export const GetBatchById = async (
    businessId: number,
    batchId: number,
    localeText: any
): Promise<ApiResponseI<BatchI>> => {
    return axios
        .get<{ data: BatchI }>(`${API_BASE_URL}/api/business/batch?businessId=${businessId}&batchId=${batchId}`)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: (res.data as any).data || res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Create batch
 */
export const CreateBatch = async (
    businessId: number,
    data: BatchI,
    localeText: any
): Promise<ApiResponseI<BatchI>> => {
    return axios
        .post<{ data: BatchI }>(`${API_BASE_URL}/api/business/batch?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: (res.data as any).data || res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Edit batch
 */
export const EditBatch = async (
    businessId: number,
    data: BatchI,
    localeText: any
): Promise<ApiResponseI<BatchI>> => {
    return axios
        .patch<{ data: BatchI }>(`${API_BASE_URL}/api/business/batch?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: (res.data as any).data || res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Delete batch
 */
export const DeleteBatch = async (
    businessId: number,
    batchId: number,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/batch?businessId=${businessId}&batchId=${batchId}`)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

export const GetSupplierBuyingPriceReference = async (
    businessId: number,
    supplierId: number,
    localeText: any,
    variantId: number,
    productId: number,
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/supplier/buyingPrice?businessId=${businessId}&supplierId=${supplierId}${variantId != null && !isNaN(Number(variantId)) ? `&variantId=${variantId}` : ''}&productId=${productId}&limit=10`)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: e?.response?.data?.message || localeText?.common?.request_error
                }
            }
        })
}

export const GetCustomerSellingPriceReference = async (
    businessId: number,
    customerId: number,
    localeText: any,
    variantId: number,
    productId: number,
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/customer/sellingPrice?businessId=${businessId}&customerId=${customerId}${variantId != null && !isNaN(Number(variantId)) ? `&variantId=${variantId}` : ''}&productId=${productId}&limit=10`)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: e?.response?.data?.message || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get batch details
 */
export const GetBatchDetails = async (
    businessId: number,
    batchId: number,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/batch/details?businessId=${businessId}&batchId=${batchId}`)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: (res.data as any).data || res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/** 
 * Update business setting
 */
export const UpdateBusinessSetting = async (
    businessId: number,
    data: { enableBatch?: boolean },
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .patch(`${API_BASE_URL}/api/business/setting?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: e?.response?.data?.message || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Assign product to batch inventory
 */
export const AssignProductToBatch = async (
    businessId: number,
    data: any,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .post(`${API_BASE_URL}/api/business/batch/inventory?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: e?.response?.data?.message || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Update product in batch inventory (Edit)
 */
export const EditProductToBatch = async (
    businessId: number,
    data: any,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .patch(`${API_BASE_URL}/api/business/batch/inventory?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: e?.response?.data?.message || localeText?.common?.request_error
                }
            }
        })
}
/**
 * Get batch details by product ID
 */
export const GetBatchDetailsByProduct = async (
    businessId: number,
    productId: number | string,
    localeText: any,
    offset: number = 0
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/batch/details-by-product`, {
            params: {
                businessId,
                productId,
                offset
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: e?.response?.data?.message || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get Dashboard Report
 */
export const GetDashboardReport = async (
    businessId: number,
    params: {
        startDate: string;
        endDate: string;
    },
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .get<{ data: any }>(
            `${API_BASE_URL}/api/business/reports/dashboard`,
            {
                params: {
                    businessId,
                    ...params
                }
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}
/**
 * Get Recent Activity
 */
export const GetRecentActivity = async (
    businessId: number,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .get<{ data: any }>(
            `${API_BASE_URL}/api/business/reports/recent-activity`,
            {
                params: {
                    businessId
                }
            }
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}


/**
 * Get all serial number
 */
export const GetSerialNumbers = async (
    businessId: number,
    localeText: any,
    variantId?: number,
    productId?: number,
    params?: { offset?: number; limit?: number; search?: string; status?: string; orderBy?: string }
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/product/serial-number`, {
            params: {
                ...params,
                businessId,
                ...(productId && { productId }),
                ...(variantId && { variantId })
            }
        })
        .then((res) => {
            if (res.status === 200) {
                // handle both {data:[], batch:[]} and [] formats
                const responseData = res.data as any;
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message

            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get serial number history (events/moves)
 */
export const GetSerialNumberHistory = async (
    businessId: number,
    serialId: number,
    localeText: any,
    params?: { orderBy?: string }
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/product/serial-number/get-history`, {
            params: {
                ...params,
                businessId,
                serialId
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get all bin locations
 */
export const GetBinLocations = async (
    businessId: number,
    warehouseId: number,
    localeText: any,
    pagination?: { limit?: number; offset?: number; variantId?: number; productId?: number }
): Promise<ApiResponseI<GetBinLocationsResponseI>> => {
    const { limit, offset, variantId, productId } = pagination || {}
    return axios
        .get(`${API_BASE_URL}/api/business/product/warehouse/bin-location`, {
            params: {
                businessId,
                warehouseId,
                ...(typeof limit === 'number' ? { limit } : {}),
                ...(typeof offset === 'number' ? { offset } : {}),
                ...(variantId ? { variantId } : {}),
                ...(productId ? { productId } : {}),
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Create bin location
 */
export const CreateBinLocation = async (
    businessId: number,
    data: { warehouseId: number; binName: string; notes?: string },
    localeText: any
): Promise<ApiResponseI<BinI>> => {
    return axios
        .post(`${API_BASE_URL}/api/business/product/warehouse/bin-location?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Edit bin location
 */
export const EditBinLocation = async (
    businessId: number,
    data: { binId: number; binName: string; notes?: string },
    localeText: any
): Promise<ApiResponseI<BinI>> => {
    return axios
        .patch(`${API_BASE_URL}/api/business/product/warehouse/bin-location?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Delete bin location
 */
export const DeleteBinLocation = async (
    businessId: number,
    binId: number,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/product/warehouse/bin-location?businessId=${businessId}`, {
            data: { binId }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get bin inventory
 */
export const GetBinInventory = async (
    businessId: number,
    warehouseId: number,
    binId: number,
    offset: number = 0,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/product/warehouse/bin-location/inventory`, {
            params: {
                businessId,
                warehouseId,
                binId,
                offset
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Bin to Bin Inventory Transfer
 */
export const BinInventoryTransfer = async (
    businessId: number,
    data: any,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .post(`${API_BASE_URL}/api/business/product/warehouse/bin-location/inventory/transfer?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}
/**
 * Get Bin Transfer History
 */
export const GetBinTransferHistory = async (
    businessId: number,
    params: any,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/product/warehouse/bin-location/inventory/transfer?businessId=${businessId}`, {
            params
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get Variant Templates
 */
export const GetVariantTemplates = async (
    businessId: number,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/variant-template`, {
            params: { businessId }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Create Variant Template
 */
export const CreateVariantTemplate = async (
    businessId: number,
    data: { templateName: string, variantsName: any[] },
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .post(`${API_BASE_URL}/api/business/variant-template?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Update Variant Template
 */
export const UpdateVariantTemplate = async (
    businessId: number,
    payload: { templateId: number; templateName: string; variantsName: any[] },
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .patch(`${API_BASE_URL}/api/business/variant-template`, payload, {
            params: { businessId }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: res.data?.message || localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Delete Variant Template
 */
export const DeleteVariantTemplate = async (
    businessId: number,
    templateId: number,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/variant-template`, {
            params: { businessId },
            data: { templateId }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: res.data?.message || localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Save / Update product variants (for edit mode)
 * POST /api/business/product/variant/v2?businessId=:businessId
 * Body: { productId: number, variants: [{ variantId?: number, name: string }] }
 */
export const SaveProductVariants = async (
    businessId: number,
    data: {
        productId: number;
        variants: { variantId?: number; name: string; safetyStock?: number | null }[];
    },
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .patch(`${API_BASE_URL}/api/business/product/variant/v2?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}


/**
 * Assign product to bin inventory
 */
export const CreateBinInventory = async (
    businessId: number,
    data: { warehouseId: number; binId: number; items: any[] },
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .post(`${API_BASE_URL}/api/business/product/warehouse/bin-location/inventory?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Update bin inventory item
 */
export const UpdateBinInventory = async (
    businessId: number,
    data: { warehouseId: number; binId: number; items: any[] },
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .patch(`${API_BASE_URL}/api/business/product/warehouse/bin-location/inventory?businessId=${businessId}`, data)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}


// Get Warehouse
export const GetON = async (
    businessId: number,
    localeText: any,
    type: 'purchase' | 'sale' = 'purchase'
): Promise<ApiResponseI<any>> => {
    const endpoint = type === 'sale' ? 'sale-order' : 'purchase-order'
    return axios
        .get<{ data: any }>(
            `${API_BASE_URL}/api/business/${endpoint}/order-number?businessId=${businessId}`
        )
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res?.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Delete product replacement
 */
export const DeleteProductReplacement = async (
    businessId: number,
    replacementId: number,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/product/productReplacement?businessId=${businessId}&replacementId=${replacementId}`)
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: true
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get product replacement by ID
 */
export const GetProductReplacementById = async (
    businessId: number,
    replacementId: number,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/product/productReplacement`, {
            params: {
                businessId,
                replacementId
            }
        })
        .then((res) => {
            if (res.status === 200) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}
// InventoryCountsApi

/**
 * Get all inventory counts for a business
 */
export const GetInventoryCount = async (
    businessId: number | string,
    params: any,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .get(
            `${API_BASE_URL}/api/business/inventoryCount?businessId=${businessId}`,
            {
                params
            }
        )
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Create a new inventory count sheet
 */
export const CreateInventoryCount = async (
    businessId: number | string,
    data: any,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .post(
            `${API_BASE_URL}/api/business/inventoryCount?businessId=${businessId}`,
            data
        )
        .then((res) => {
            if (res.status === 200 || res.status === 201 || res.data?.success) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Update an existing inventory count sheet
 */
export const UpdateInventoryCount = async (
    businessId: number | string,
    inventoryCountId: number | string,
    data: any,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .patch(
            `${API_BASE_URL}/api/business/inventoryCount?businessId=${businessId}&inventoryCountId=${inventoryCountId}`,
            data
        )
        .then((res) => {
            if (res.status === 200 || res.status === 201 || res.data?.success) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Delete inventory count
 */
export const DeleteInventoryCount = async (
    businessId: number | string,
    inventoryCountId: number | string,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/inventoryCount?businessId=${businessId}&inventoryCountId=${inventoryCountId}`)
        .then((res) => {
            if (res.status === 200 || res.status === 204 || res.data?.success) {
                return {
                    data: true
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Create task sheet
 */
export const CreateTaskSheet = async (
    businessId: number | string,
    inventoryCountId: number | string,
    data: { title: string; assigneeId: number | string },
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .post(
            `${API_BASE_URL}/api/business/inventoryCount/task-sheet?businessId=${businessId}&inventoryCountId=${inventoryCountId}`,
            data
        )
        .then((res) => {
            if (res.status === 200 || res.status === 201 || res.data?.success) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get task sheets list
 */
export const GetTaskSheets = async (
    businessId: number | string,
    inventoryCountId: number | string,
    params: { taskSheetId?: number | string; assigneeId?: number | string } = {},
    localeText: any
): Promise<ApiResponseI<any>> => {
    let query = `?businessId=${businessId}&inventoryCountId=${inventoryCountId}`
    if (params.taskSheetId) {
        query += `&taskSheetId=${params.taskSheetId}`
    }
    if (params.assigneeId) {
        query += `&assigneeId=${params.assigneeId}`
    }

    return axios
        .get(`${API_BASE_URL}/api/business/inventoryCount/task-sheet${query}`)
        .then((res) => {
            if (res.status === 200 || res.data?.success) {
                return {
                    data: res.data
                }
            }

            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)

            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Delete task sheet
 */
export const DeleteTaskSheet = async (
    businessId: number | string,
    taskSheetId: number | string,
    localeText: any
): Promise<ApiResponseI<boolean>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/inventoryCount/task-sheet?businessId=${businessId}&taskSheetId=${taskSheetId}`)
        .then((res) => {
            if (res.status === 200 || res.status === 204 || res.data?.success) {
                return {
                    data: true
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Update task sheet details (title, assignee, draft status)
 */
export const UpdateTaskSheet = async (
    businessId: number | string,
    inventoryCountId: number | string,
    data: { taskSheetId: number | string; title: string; assigneeId: number | string; status?: string; products?: any[] },
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .patch(
            `${API_BASE_URL}/api/business/inventoryCount/task-sheet/detail?businessId=${businessId}&inventoryCountId=${inventoryCountId}`,
            data
        )
        .then((res) => {
            if (res.status === 200 || res.status === 201 || res.data?.success) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Complete task sheet — submits with products payload
 * PATCH /api/business/inventoryCount/task-sheet
 */
export const CompleteTaskSheet = async (
    businessId: number | string,
    inventoryCountId: number | string,
    data: { taskSheetId: number | string; status: string; products?: any[] },
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .patch(
            `${API_BASE_URL}/api/business/inventoryCount/task-sheet?businessId=${businessId}&inventoryCountId=${inventoryCountId}`,
            data
        )
        .then((res) => {
            if (res.status === 200 || res.status === 201 || res.data?.success) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Submit inventory count
 * POST /api/business/inventoryCount/submit?businessId=...
 */
export const SubmitInventoryCount = async (
    businessId: number | string,
    inventoryCountId: number | string,
    localeText: any
): Promise<ApiResponseI<any>> => {
    return axios
        .post(
            `${API_BASE_URL}/api/business/inventoryCount/submit?businessId=${businessId}`,
            {
                inventoryCountId
            }
        )
        .then((res) => {
            if (res.status === 200 || res.status === 201 || res.data?.success) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get products for an inventory count
 * GET /api/business/inventoryCount/product?businessId=...&inventoryCountId=...&title=...
 */
export const GetInventoryCountProducts = async (
    businessId: number | string,
    inventoryCountId: number | string,
    title?: string,
    localeText?: any
): Promise<ApiResponseI<any>> => {
    let url = `${API_BASE_URL}/api/business/inventoryCount/product?businessId=${businessId}&inventoryCountId=${inventoryCountId}`
    if (title) {
        url += `&title=${encodeURIComponent(title)}`
    }
    return axios
        .get(url)
        .then((res) => {
            if (res.status === 200 || res.status === 201 || res.data?.success) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get inventory count history
 * GET /api/business/inventoryCount/history?businessId=...&inventoryCountId=...
 */
export const GetInventoryCountHistory = async (
    businessId: number | string,
    inventoryCountId: number | string,
    localeText?: any
): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/inventoryCount/history?businessId=${businessId}&inventoryCountId=${inventoryCountId}`)
        .then((res) => {
            if (res.status === 200 || res.status === 201 || res.data?.success) {
                return {
                    data: res.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Get customer cash report
 */
export const GetCustomerCashReport = async (
    businessId: number | string,
    params: { startDate: string; endDate: string; limit?: number; offset?: number; sortBy?: string; orderBy?: string, timezone?: string },
    localeText?: any
): Promise<ApiResponseI<CashReportResponse>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/customer/cash-report`, {
            params: {
                businessId,
                ...params
            }
        })
        .then((res) => {
            if (res.status === 200 || res.status === 201 || res.data?.success) {
                return {
                    data: res.data.data
                }
            }
            return {
                error: {
                    message: localeText?.common?.request_error
                }
            }
        })
        .catch((e) => {
            $error(e)
            const error = e?.response?.data?.message
            return {
                error: {
                    message: error || localeText?.common?.request_error
                }
            }
        })
}

/**
 * Invoice Address APIs
 */
export const GetInvoiceAddress = async (businessId: number | string): Promise<ApiResponseI<any[]>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/invoice-address`, {
            params: { businessId }
        })
        .then((res: any) => {
            if (res.status === 200) {
                return {
                    data: res.data?.data || []
                }
            }
            return {
                error: {
                    message: 'Failed to fetch invoice addresses'
                }
            }
        })
        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: e?.response?.data?.message || 'Failed to fetch invoice addresses'
                }
            }
        })
}

export const CreateInvoiceAddressApi = async (businessId: number | string, data: any): Promise<ApiResponseI<any>> => {
    return axios
        .post(`${API_BASE_URL}/api/business/invoice-address?businessId=${businessId}`, data)
        .then((res: any) => {
            if (res.status === 200 || res.status === 201) {
                return {
                    data: res.data?.data
                }
            }
            return {
                error: {
                    message: 'Failed to create invoice address'
                }
            }
        })
        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: e?.response?.data?.message || 'Failed to create invoice address'
                }
            }
        })
}

export const UpdateInvoiceAddressApi = async (businessId: number | string, data: any): Promise<ApiResponseI<any>> => {
    return axios
        .patch(`${API_BASE_URL}/api/business/invoice-address?businessId=${businessId}`, data)
        .then((res: any) => {
            if (res.status === 200) {
                return {
                    data: res.data?.data
                }
            }
            return {
                error: {
                    message: 'Failed to update invoice address'
                }
            }
        })
        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: e?.response?.data?.message || 'Failed to update invoice address'
                }
            }
        })
}

export const DeleteInvoiceAddressApi = async (businessId: number | string, id: string | number): Promise<ApiResponseI<any>> => {
    return axios
        .delete(`${API_BASE_URL}/api/business/invoice-address`, {
            params: { businessId, id }
        })
        .then((res: any) => {
            if (res.status === 200) {
                return {
                    data: res.data?.data
                }
            }
            return {
                error: {
                    message: 'Failed to delete invoice address'
                }
            }
        })
        .catch((e) => {
            $error(e)
            return {
                error: {
                    message: e?.response?.data?.message || 'Failed to delete invoice address'
                }
            }
        })
}


const extractImageUrls = (resData: any): string[] => {
    const rawList = resData?.data?.images || resData?.images || resData?.data || resData?.result || resData || []
    if (!Array.isArray(rawList)) return []
    return rawList
        .map((item: any) => (typeof item === 'string' ? item : item?.imageUrl || item?.url || ''))
        .filter((url: string) => Boolean(url) && typeof url === 'string')
}

export const ProductImageGenerate = async (query: { businessId: any | number; name: string }, localeText?: any): Promise<ApiResponseI<string[]>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/product/product-images`, { params: query })
        .then((res) => {
            if (res.status === 200) {
                const urls = extractImageUrls(res.data)
                return {
                    data: urls
                }
            }
            return {
                error: { message: localeText?.common?.request_error || 'Failed to fetch AI images' }
            }
        })
        .catch((e) => {
            $error(e)
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to fetch AI images' },
                data: []
            }
        })
}

export const productImageGenerate = ProductImageGenerate










export const SyncProductsFromTally = async (businessId: string | number, tallyResponse: any, localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .post(`${API_BASE_URL}/api/business/product/mapping/sync/from-tally?businessId=${businessId}`, { tallyResponse })
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to sync products from Tally' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to sync products from Tally' }
            }
        })
}

export const GetProductsToSyncToTally = async (businessId: string | number, localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/product/mapping/sync/to-tally?businessId=${businessId}`)
        .then((res) => {
            if (res.status === 200) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to get products to sync to Tally' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to get products to sync to Tally' }
            }
        })
}

export const UpdateTallySyncStatus = async (businessId: string | number, products: any[], localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .patch(`${API_BASE_URL}/api/business/product/mapping/sync/tally-status?businessId=${businessId}`, { products })
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to update Tally sync status' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to update Tally sync status' }
            }
        })
}

export const SyncSaleOrdersFromTally = async (businessId: string | number, tallyResponse: any, localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .post(`${API_BASE_URL}/api/business/sale-order/mapping/sync/from-tally?businessId=${businessId}`, { tallyResponse })
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to sync sale orders from Tally' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to sync sale orders from Tally' }
            }
        })
}

export const GetSaleOrdersToSyncToTally = async (businessId: string | number, localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/sale-order/mapping/sync/to-tally?businessId=${businessId}`)
        .then((res) => {
            if (res.status === 200) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to get sale orders to sync to Tally' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to get sale orders to sync to Tally' }
            }
        })
}

export const UpdateTallySaleOrderSyncStatus = async (businessId: string | number, saleOrders: any[], localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .patch(`${API_BASE_URL}/api/business/sale-order/mapping/sync/tally-status?businessId=${businessId}`, { saleOrders })
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to update Tally sale order sync status' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to update Tally sale order sync status' }
            }
        })
}

export const SyncCustomersFromTally = async (businessId: string | number, tallyResponse: any, localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .post(`${API_BASE_URL}/api/business/customer-mapping/sync/from-tally?businessId=${businessId}`, { tallyResponse })
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to sync customers from Tally' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to sync customers from Tally' }
            }
        })
}

export const GetCustomersToSyncToTally = async (businessId: string | number, localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/customer-mapping/sync/to-tally?businessId=${businessId}`)
        .then((res) => {
            if (res.status === 200) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to get customers to sync to Tally' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to get customers to sync to Tally' }
            }
        })
}

export const UpdateTallyCustomerSyncStatus = async (businessId: string | number, customers: any[], localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .patch(`${API_BASE_URL}/api/business/customer-mapping/sync/tally-status?businessId=${businessId}`, { customers })
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to update Tally customer sync status' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to update Tally customer sync status' }
            }
        })
}

export const SyncPurchaseOrdersFromTally = async (businessId: string | number, tallyResponse: any, localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .post(`${API_BASE_URL}/api/business/purchase-order/mapping/sync/from-tally?businessId=${businessId}`, { tallyResponse })
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to sync purchase orders from Tally' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to sync purchase orders from Tally' }
            }
        })
}

export const GetPurchaseOrdersToSyncToTally = async (businessId: string | number, localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/purchase-order/mapping/sync/to-tally?businessId=${businessId}`)
        .then((res) => {
            if (res.status === 200) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to get purchase orders to sync to Tally' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to get purchase orders to sync to Tally' }
            }
        })
}

export const UpdateTallyPurchaseOrderSyncStatus = async (businessId: string | number, purchaseOrders: any[], localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .patch(`${API_BASE_URL}/api/business/purchase-order/mapping/sync/tally-status?businessId=${businessId}`, { purchaseOrders })
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to update Tally purchase order sync status' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to update Tally purchase order sync status' }
            }
        })
}

export const SyncSuppliersFromTally = async (businessId: string | number, tallyResponse: any, localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .post(`${API_BASE_URL}/api/business/supplier/mapping/sync/from-tally?businessId=${businessId}`, { tallyResponse })
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to sync suppliers from Tally' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to sync suppliers from Tally' }
            }
        })
}

export const GetSuppliersToSyncToTally = async (businessId: string | number, localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .get(`${API_BASE_URL}/api/business/supplier/mapping/sync/to-tally?businessId=${businessId}`)
        .then((res) => {
            if (res.status === 200) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to get suppliers to sync to Tally' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to get suppliers to sync to Tally' }
            }
        })
}

export const UpdateTallySupplierSyncStatus = async (businessId: string | number, suppliers: any[], localeText?: any): Promise<ApiResponseI<any>> => {
    return axios
        .patch(`${API_BASE_URL}/api/business/supplier/mapping/sync/tally-status?businessId=${businessId}`, { suppliers })
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to update Tally supplier sync status' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to update Tally supplier sync status' }
            }
        })
}


export const GetHsnList = async (
    localeText?: any,
    params?: { type?: string; search?: string; page?: number; limit?: number }
): Promise<ApiResponseI<any>> => {
    const { type = '', search = '', page = 1, limit = 20 } = params || {}
    return axios
        .get(`${API_BASE_URL}/api/business/product/hsn?type=${type}&search=${search}&page=${page}&limit=${limit}`)
        .then((res) => {
            if (res.status === 200) {
                return { data: res.data }
            }
            return { error: { message: localeText?.common?.request_error || 'Failed to get HSN/SAC list' } }
        })
        .catch((e) => {
            return {
                error: { message: e?.response?.data?.message || localeText?.common?.request_error || 'Failed to get HSN/SAC list' }
            }
        })
}
